import { isDevMode } from '@angular/core';
import {
  AbcCore,
  CollectionHelper,
  CompareHelper,
  ConvertHelper,
  DotNetBridge,
  ObjectHelper,
} from '@barthauer/abc/core';
import {
  CacheStorageAdapter,
  DataRowInfo,
  DataService,
  NetworkTraceModel,
  NetworkTraceObject,
  QueryObjectsModel,
  QueryValueModel,
} from '@barthauer/abc/core/data';
import { ParameterDefinition, ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { ParameterService } from 'libs/abc/queries/parameters/values-editor/src/lib/services/parameter.service';
import { forkJoin, from, lastValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { MapNetTracer } from './map.net-tracer';
import { MapOsmStyle } from './map.osm-style';
import { MapQueryCache } from './map.query-cache';
import { MapSelection } from './map.selection';
import { MapTileCache } from './map.tile-cache';
import { MapVectorStyle } from './map.vector-style';
import { MapView } from './map.view';
import { DataEntity, DataEntityInfo } from './models/element.model';
import {
  Map as MapDefinition,
  MapLayer,
  MapLayerType,
  MapStyle,
  MapStyleFill,
  MapStyleImage,
  MapStyleStroke,
  MapStyleText,
  MapStyleValue,
  MapTarget,
} from './models/map.model';
import { QueryMapModel } from './models/query.model';
import { MapService } from './services/map.service';
import { MapLabelPlacer } from './map.label-placer';
import { AbcConfigService } from '@barthauer/abc/core/configuration';

declare let ol: any;
declare let proj4: any;

export class MapLoader {
  private geoJsonFormat: any;
  private mvtFormat: any;
  private olmap: any;
  private enforcedViewProjection: any;
  private dataViewProjection: any;
  private packageId: string;
  private mapNames: string[];
  private scales: number[];
  private initialViewbox: number[];
  private selectionColor: number[];
  private customObjectColors: Map<string, number[]>;
  public mapSelection: MapSelection;
  private resolutionFactor: number;
  private tileCache: MapTileCache;
  private tileCacheSources: Map<string, any>;
  private queryCache: MapQueryCache;
  private queryCacheSources: Map<string, any>;
  private layersWithLoadErrors: MapLayer[] = new Array<MapLayer>();
  private layersCurrentlyLoading: MapLayer[] = new Array<MapLayer>();
  private renderMapDpi: number;
  private currentMapQueryParameters: ParameterValue[];
  private dataEntitiesToRefresh: DataEntity[] = [];
  public currentCacheFilterExpression: string;
  public hasTooltips: boolean;
  private lastMapLabelPlacer: MapLabelPlacer;
  private initialRotationValue: number;
  public definition: MapDefinition;

  constructor(
    private abcCore: AbcCore,
    private coreDataService: DataService,
    private configService: AbcConfigService,
    private readMapUrl: string,
    private readMapImageUrl: string,
    private transformMapValueUrl: string,
    private mapId: any,
    private overviewId: any,
    private attributionId: any,
    private mapScaleId: any,
    private rotateId: any,
    private mapService: MapService,
    private parameterService: ParameterService,
    private res: ResourceSet,
  ) {
    this.geoJsonFormat = new ol.format.GeoJSON();
    this.mvtFormat = new ol.format.MVT();

    this.mapService.getWebApiBaseURL = () => this.configService.getWebApiBaseURL();
    this.mapService.getDataEntityInfos = (entities) => this.getDataEntityInfos(entities);

    this.mapService.getTargetOfObjectSet = (name) => this.getTargetOfObjectSet(name);

    this.mapService.getCurrentQueryParameters = () => this.deepClone(this.currentMapQueryParameters);

    CacheStorageAdapter.isOfflineChanged.subscribe((offline) => {
      if (this.olmap) {
        if (!offline || CacheStorageAdapter.isDownloadComplete) {
          for (const ly of this.getAllLayers()) {
            if (ly.getSource) {
              const src = ly.getSource();
              if (src && src.refresh) {
                src.refresh();
              }
            }
          }
        }
      }
    });
  }

  public loadMap(
    readMapMessage: QueryMapModel,
    defaultExtent: number[],
    initialExtent: number[],
    imageVectorScaleLimit: number,
    minScaleLimit: number,
    cacheFilterExpression?: string,
  ): Observable<any> {
    return this.coreDataService.executeWebApiPOSTMethod(this.readMapUrl, readMapMessage).pipe(
      concatMap(async (data) => {
        const m = data as any as MapDefinition;
        let parameterDefinitions = this.mergeParameterDefinitions(
          m.dataSources?.filter((_) => _.useCurrentDataStore === true)?.map((_) => _?.ParameterDefinitions),
        );
        const parameterNamesToExclude = readMapMessage?.Parameters?.map((_) => _.Name);
        const effectiveParameters = this.parameterService.tryFindChangeableParameters(
          parameterDefinitions,
          parameterNamesToExclude,
        );
        if (effectiveParameters?.length) {
          const paramaterValues = await this.parameterService.fillParametersIfExist(effectiveParameters);
          return await this.loadMapWithParameters(
            m,
            readMapMessage,
            defaultExtent,
            initialExtent,
            imageVectorScaleLimit,
            minScaleLimit,
            cacheFilterExpression,
            paramaterValues,
          ).toPromise();
        } else {
          return await this.loadMapWithParameters(
            m,
            readMapMessage,
            defaultExtent,
            initialExtent,
            imageVectorScaleLimit,
            minScaleLimit,
            cacheFilterExpression,
            undefined,
          ).toPromise();
        }
      }),
    );
  }

  private loadMapWithParameters(
    m: MapDefinition,
    readMapMessage: QueryMapModel,
    defaultExtent: number[],
    initialExtent: number[],
    imageVectorScaleLimit: number,
    minScaleLimit: number,
    cacheFilterExpression: string,
    filledParameters: ParameterValue[],
  ) {
    const osmlayer = new MapLayer();
    osmlayer.name = 'osm';
    osmlayer.type = 'OSM';
    //osmlayer.type = "XYZ";
    osmlayer.opacity = 0.4;
    return from(
      this.loadMapCore(
        readMapMessage?.PackageId,
        readMapMessage?.MapNames,
        m,
        [],
        defaultExtent,
        initialExtent,
        imageVectorScaleLimit,
        minScaleLimit,
      ),
    ).pipe(
      tap((_) => {
        this.queryCache
          ?.tryLoadColors((key, color) => {
            if (!this.customObjectColors) {
              this.customObjectColors = new Map<string, number[]>();
            }
            this.customObjectColors.set(key, color);
          })
          .then(() => {});

        this.currentMapQueryParameters = this.mergeQueryParameters(readMapMessage?.Parameters, filledParameters);
        this.currentCacheFilterExpression = cacheFilterExpression;
        this.olmap = _;
        const targetUrls: { layer: MapLayer; url: string }[] = [];
        for (const ly of this.getAllLayers()) {
          if (ly.layerdefinition?.target?.url) {
            targetUrls.push({ layer: ly.layerdefinition, url: ly.layerdefinition?.target?.url });
          }
        }
        if (targetUrls?.length) {
          const func = (ev) => {
            const prefix = 'map:';
            if (!ev.data.name?.startsWith(prefix)) {
              return;
            }
            const funcName = ev.data.name.substring(prefix.length);
            let entites: DataEntity[];
            let rows: DataRowInfo[];
            for (const t of targetUrls) {
              if (t.url.startsWith('{baseurl}') || t.url.startsWith(ev.origin)) {
                if (funcName === 'select') {
                  if (t.layer.type === MapLayerType[MapLayerType.ObjectSpace]) {
                    if (!entites) {
                      entites = [];
                    }
                    for (const e of ev.data.arg) {
                      const id: string = e?.id;
                      const objectType: string | number = e?.objectType;
                      if (id && objectType) {
                        entites.push(new DataEntity(objectType, id));
                      }
                    }
                  } else {
                    if (!rows) {
                      rows = [];
                    }
                    const ids: any[] = ev.data.arg;
                    for (const id of ids) {
                      rows.push({ DataSetName: t.layer.fullName, Id: id });
                    }
                  }
                } else {
                  alert('Invalid message type:' + ev.data.name);
                  break;
                }
              }
            }
            if (entites) {
              this.mapSelection.moveToObjects(entites);
            }
            if (rows) {
              this.mapSelection.moveToDataRows(rows);
            }
          };

          if (window.addEventListener) {
            window.addEventListener('message', func.bind(this), false);
          } else {
            (<any>window).attachEvent('onmessage', func.bind(this));
          }
        }
      }),
    );
  }

  private mergeQueryParameters(parameters: ParameterValue[], otherParameters: ParameterValue[]): ParameterValue[] {
    if (parameters?.length > 0 && otherParameters?.length > 0) {
      var result: ParameterValue[] = this.deepClone(parameters);
      for (const p of otherParameters) {
        if (!result.find((_) => _.Name === p.Name)) {
          result.push(p);
        }
      }
      return result;
    } else if (parameters?.length > 0) {
      return parameters;
    } else {
      return otherParameters;
    }
  }

  private mergeParameterDefinitions(parameters: ParameterDefinition[][]): ParameterDefinition[] {
    if (parameters?.length > 1) {
      var result: ParameterDefinition[] = this.deepClone(parameters[0]);
      for (let i = 1; i < parameters.length; i++) {
        for (const p of parameters[i]) {
          if (!result.find((_) => _.Name === p.Name)) {
            result.push(p);
          }
        }
      }
      return result;
    } else if (parameters?.length > 0) {
      return parameters[0];
    }
  }

  public getScales(): number[] {
    return this.scales;
  }

  private async loadMapCore(
    packageId: string,
    mapNames: string[],
    map: MapDefinition,
    defaultLayers: MapLayer[],
    defaultExtent: number[],
    initialExtent: number[],
    imageVectorScale: number,
    minScale: number,
  ): Promise<any> {
    this.packageId = packageId;
    this.mapNames = mapNames;
    this.scales = map.scales;
    if (!this.scales || !this.scales.length) {
      this.scales = [250, 500, 1000, 2000, 2500, 5000, 10000, 20000, 100000, 500000];
    }
    this.selectionColor = map.view?.selectionColor;
    if (!this.selectionColor) {
      this.selectionColor = [0, 255, 0];
    }

    this.tileCacheSources = new Map<string, any>();
    this.queryCacheSources = new Map<string, any>();

    //PIMS: "+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +units=m +no_defs"
    for (const proj of map.projections) {
      proj4.defs(this.getProjectionName(proj.name), proj.definition);
    }
    ol.proj.proj4.register(proj4);

    let mapProjection = null;
    const view = map.view;
    if (view && view.projection) {
      mapProjection = this.getProjection(view.projection);
      if (mapProjection && view.extent && view.extent[0] >= 10000000) {
        this.enforceDefaultViewProjection();
      }
    }
    let layers = map.layers;
    if (defaultLayers) {
      layers = layers.concat(defaultLayers);
    }

    const allLayers = MapLoader.getAllMapLayers(map);
    const useLabelPlacing: boolean = !!allLayers.find((_) => _.style?.find((_) => this.useLabelPlacing(_)));
    const ollayers = await this.createLayers(
      layers,
      false,
      imageVectorScale,
      minScale,
      mapProjection,
      null,
      useLabelPlacing,
    );
    const oloverviewlayers = await this.createLayers(
      map.overviewLayers,
      true,
      imageVectorScale,
      minScale,
      mapProjection,
      null,
      false,
    );

    const customcontrols = [];
    customcontrols.push(
      new ol.control.MousePosition({
        className: 'ol-mouse-position ol-custom-mouse-position',
        coordinateFormat: ol.coordinate.createStringXY(3),
        undefinedHTML: '&nbsp;',
        projection: mapProjection,
      }),
    );

    customcontrols.push(
      new ol.control.ScaleLine({
        //className: "ol-custom-scale-line ol-scale-line", //keep the order! "ol-scale-line" have to be at the end!
        bar: true,
        text: true,
        steps: 4,
        target: this.mapScaleId,
        dpi: MapLoader.getDpi(),
        minWidth: 130,
      }),
    );

    customcontrols.push(
      new ol.control.Rotate({
        target: this.rotateId,
      }),
    );

    this.initialViewbox = view.extent;

    let extent = initialExtent;
    if (!extent?.length) {
      extent = view.extent;
    }
    if (!extent?.length) {
      extent = defaultExtent;
    }

    if (this.enforcedViewProjection) {
      if (extent) {
        extent = ol.proj.transformExtent(extent, mapProjection, this.enforcedViewProjection);
      }
      if (this.initialViewbox) {
        this.initialViewbox = ol.proj.transformExtent(this.initialViewbox, mapProjection, this.enforcedViewProjection);
      }
      this.dataViewProjection = mapProjection;
      mapProjection = this.enforcedViewProjection;
    }
    if (extent) {
      const center = [(extent[2] + extent[0]) / 2.0, (extent[3] + extent[1]) / 2.0];
      const pr = ol.proj.getPointResolution(mapProjection, 1, center);
      this.resolutionFactor = pr;

      MapLoader.updateLayerResolutions(this.getLayersRecursive(ollayers, false), 1.0 / this.resolutionFactor);
    }

    if (oloverviewlayers?.length > 0) {
      const oloverviewview = new ol.View({
        projection: mapProjection,
        enableRotation: false,
      });

      const overviewMapControl = new ol.control.OverviewMap({
        className: 'ol-overviewmap ol-custom-overviewmap',
        expandClassName: 'ol-custom-overviewmap-expand',
        collapseClassName: 'ol-custom-overviewmap-collapse',
        layers: oloverviewlayers,
        collapsed: true,
        collapseLabel: '\u2346',
        label: '\u2347',
        tipLabel: 'Übersichtskarte',
        target: this.overviewId,
        view: oloverviewview,
      });
      customcontrols.push(overviewMapControl);

      if (view && extent) oloverviewview.fit(extent);
    }

    this.hasTooltips = allLayers.find((_) => _.columns?.find((_) => _.useForTooltip)) ? true : false;
    this.definition = map;

    if (allLayers.find((_) => _.copyright)) {
      const attributionControl = new ol.control.Attribution({
        className: 'ol-attribution ol-custom-attribution',
        expandClassName: 'ol-custom-attribution-expand',
        collapseClassName: 'ol-custom-attribution-collapse',
        collapsible: true,
        collapsed: true,
        collapseLabel: '\u2B6D',
        label: '\u00A9',
        tipLabel: 'Copyright',
        target: this.attributionId,
      });
      customcontrols.push(attributionControl);

      this.mapService.getVisibleAttributions = () => this.getVisibleAttributions();
    }

    const defaults = ol.control.defaults.defaults;
    const controls = defaults({
      attribution: false,
      attributionOptions: {
        collapsible: false,
      },
      rotate: true,
      rotateOptions: {
        autoHide: true,
      },
      zoom: false,
      zoomOptions: {
        autoHide: true,
      },
    }).extend(customcontrols);

    const olview = new ol.View({
      projection: mapProjection,
      zoomFactor: 1.5,
      enableRotation: true,
    });

    const olmap = new ol.Map({
      controls: controls,
      layers: ollayers,
      target: this.mapId,
      view: olview,
    });

    if (view && extent) {
      if (this.tileCacheSources.size > 0) {
        this.tileCache = new MapTileCache(
          this,
          this.abcCore.cache.storage,
          olmap,
          mapProjection,
          this.initialViewbox,
          this.tileCacheSources,
        );
        this.abcCore.cache.storage.items.push(this.tileCache.createMapCacheItem());
      }

      if (this.queryCacheSources.size > 0) {
        this.queryCache = new MapQueryCache(
          this,
          this.abcCore.cache.storage,
          olmap,
          this.initialViewbox,
          this.queryCacheSources,
        );
        this.abcCore.cache.storage.items.push(this.queryCache.createMapCacheItem());
      }

      const fitAction: () => void = () => {
        olview.fit(extent);
        const params = new URLSearchParams(document.location.search);
        let x: number;
        let y: number;
        let worldx: number;
        let worldy: number;
        let scale: number;
        let rotation: number;
        let objecttype: string;
        let objectids: string[];
        let command: string;
        for (const [key, value] of params.entries()) {
          const k = key?.toLowerCase();
          if (k === 'x') {
            x = ConvertHelper.toNumber(value);
          } else if (k === 'y') {
            y = ConvertHelper.toNumber(value);
          } else if (k === 'worldx') {
            worldx = ConvertHelper.toNumber(value);
          } else if (k === 'worldy') {
            worldy = ConvertHelper.toNumber(value);
          } else if (k === 'scale') {
            scale = ConvertHelper.toNumber(value);
          } else if (k === 'rotation') {
            rotation = ConvertHelper.toNumber(value);
          } else if (k === 'objecttype') {
            objecttype = value;
          } else if (k === 'objectid' && value) {
            objectids = [value];
          } else if (k === 'objectids') {
            objectids = value?.split(',')?.filter((_) => _);
          } else if (k === 'command') {
            command = value;
          }
        }
        let replaceState = false;
        if (
          ((!CompareHelper.isNullorEmpty(x) && !CompareHelper.isNullorEmpty(y)) ||
            (!CompareHelper.isNullorEmpty(worldx) && !CompareHelper.isNullorEmpty(worldy))) &&
          !CompareHelper.isNullorEmpty(scale)
        ) {
          const vw = this.createMapView();
          if (CompareHelper.isNullorEmpty(x) || CompareHelper.isNullorEmpty(y)) {
            vw.setCenterWithWorldProjection([worldx, worldy]);
          } else {
            vw.setCenterWithDataProjection([x, y]);
          }
          vw.setCurrentScale(scale);
          if (rotation) {
            this.initialRotationValue = (rotation / 180.0) * Math.PI;
            if (vw.getRotationEnabled()) {
              vw.setRotation(this.initialRotationValue);
            }
          }
          replaceState = true;
        }
        let entities: DataEntity[];
        if (!CompareHelper.isNullorEmpty(objecttype) && objectids?.length) {
          entities = objectids.map((id) => new DataEntity(objecttype, id));
          this.mapSelection.moveToObjects(entities, true);
          replaceState = true;
        }
        if (command && this.mapService.executeCommand) {
          this.mapService.executeCommand(command, entities);
        }
        if (replaceState) {
          window.history.replaceState(
            undefined,
            undefined,
            document.location.href.replace(document.location.search, ''),
          );
        }
      };

      if (this.tileCache) {
        this.tileCache.initialize().then(() => {
          fitAction();
        });
      } else {
        fitAction();
      }
    }

    return olmap;
  }

  private enforceDefaultViewProjection() {
    if (!this.enforcedViewProjection) {
      this.enforcedViewProjection = ol.proj.get('EPSG:3857');
    }
  }

  public getQueryCache(): MapQueryCache {
    return this.queryCache;
  }

  private getVisibleAttributions(): string[] {
    const visibleAttributions = [];

    const lookup = {};
    var layers = this.getAllLayers();
    for (let ly of layers) {
      if (!ly.getVisible()) {
        continue;
      }

      const source = ly.getSource();
      if (!source) {
        continue;
      }

      const attributionGetter = source.getAttributions();
      if (!attributionGetter) {
        continue;
      }

      const attributions = attributionGetter(null);
      if (!attributions) {
        continue;
      }

      if (Array.isArray(attributions)) {
        for (let j = 0, jj = attributions.length; j < jj; ++j) {
          if (!(attributions[j] in lookup)) {
            visibleAttributions.push(attributions[j]);
            lookup[attributions[j]] = true;
          }
        }
      } else {
        if (!(attributions in lookup)) {
          visibleAttributions.push(attributions);
          lookup[attributions] = true;
        }
      }
    }
    return visibleAttributions;
  }

  private static getAllMapLayers(map: MapDefinition): MapLayer[] {
    const arr: MapLayer[] = [];
    this.addAllMapLayers(arr, map.layers);
    return arr;
  }

  private static addAllMapLayers(target: MapLayer[], source: MapLayer[]): void {
    if (source) {
      for (let ml of source) {
        target.push(ml);
        this.addAllMapLayers(target, ml.layers);
      }
    }
  }

  private getProjectionName(name: string): string {
    if (name.endsWith('|NE')) {
      return name.substring(0, name.length - 3);
    }
    return name;
  }

  private getProjection(name: string): any {
    const projname = this.getProjectionName(name);
    const olprojection = ol.proj.get(projname);
    if (olprojection && projname !== name) {
      olprojection.axisOrientation_ = 'ne';
    }
    return olprojection;
  }

  public createMapView(): MapView {
    const view = new MapView(
      this.olmap,
      this.mapService,
      this.dataViewProjection,
      this.enforcedViewProjection,
      this.initialViewbox,
      async () => this.initialRotationValue,
      this.res,
    );
    view.initializeRenderer(this);
    return view;
  }

  private async createLayers(
    layers: MapLayer[],
    ignoreScales: boolean,
    imageVectorScale: number,
    minScale: number,
    mapProjection: any,
    parentName: string,
    useLabelPlacing: boolean,
  ): Promise<Array<any>> {
    const ollayers = [];
    if (layers) {
      for (const layer of layers) {
        const ollayer = await this.createLayer(
          layer,
          ignoreScales,
          imageVectorScale,
          minScale,
          mapProjection,
          parentName,
          useLabelPlacing,
        );
        if (ollayer) {
          ollayers.push(ollayer);
        }
      }
    }
    return ollayers;
  }

  private async createLayer(
    layer: MapLayer,
    ignoreScales: boolean,
    imageVectorScale: number,
    minScale: number,
    mapProjection: any,
    parentName: string,
    useLabelPlacing: boolean,
  ): Promise<any> {
    let ollayer = null;
    let layerProjection = mapProjection;
    if (layer.projection) {
      layerProjection = this.getProjection(layer.projection);
    }
    const defaultRatio = 1.2;
    let defaultResolutions: number[] = null;

    const options = this.createLayerOptions(layer, ignoreScales);
    let name = layer.name;
    if (parentName) {
      name = parentName + '|' + name;
    }

    if (layer.type === MapLayerType[MapLayerType.Group]) {
      options.layers = await this.createLayers(
        layer.layers,
        ignoreScales,
        imageVectorScale,
        minScale,
        layerProjection,
        name,
        useLabelPlacing,
      );

      ollayer = new ol.layer.Group(options);
    } else if (layer.type === MapLayerType[MapLayerType.OSM]) {
      options.source = new ol.source.OSM({
        attributions: layer.copyright,
        tileLoadFunction: function (imageTile, src) {
          if (CacheStorageAdapter.useOfflineMode) {
            src = '';
          }
          if (!this.tileCache) {
            imageTile.getImage().src = src;
          } else {
            this.tileCache.tryLoadTile(name, imageTile.tileCoord).then((_) => {
              imageTile.getImage().src = _ ?? src;
            });
          }
        }.bind(this),
      });
      options.source.layerdefinition = layer;
      this.tileCacheSources.set(name, options.source);

      ollayer = new ol.layer.Tile(options);
    } else if (layer.type === MapLayerType[MapLayerType.WMS]) {
      let url: string;
      let version: string;
      let format: string;
      let layer_id: string;
      let forceBasicAuth: string;
      let corsproxy: string;
      let realurl: string;
      if (layer.dataSource) {
        url = layer.dataSource?.split('?')[0];
        try {
          let fullurl = layer.dataSource;
          const idx = fullurl.indexOf('/CorsProxy/');
          if (idx >= 0) {
            corsproxy = fullurl.substring(0, idx + '/CorsProxy/'.length);
            const base64 = fullurl.substring(idx + '/CorsProxy/'.length);
            fullurl = atob(base64);
            realurl = fullurl;
          } else {
            realurl = url;
          }
          const urlObject = new URL(fullurl);
          version = urlObject.searchParams.get('version') ?? urlObject.searchParams.get('VERSION');
          format = urlObject.searchParams.get('format') ?? urlObject.searchParams.get('FORMAT');
          layer_id = urlObject.searchParams.get('layer_id') ?? urlObject.searchParams.get('LAYER_ID');
          forceBasicAuth =
            urlObject.searchParams.get('forceBasicAuth') ??
            urlObject.searchParams.get('forcebasicauth') ??
            urlObject.searchParams.get('FORCEBASICAUTH');
        } catch {}
      }
      const params: any = { LAYERS: layer.layerNames?.join(',') };
      if (version) {
        params.VERSION = version;
      }
      if (format) {
        params.FORMAT = format;
      }
      if (layer_id) {
        try {
          let capaurl = realurl;
          if (corsproxy) {
            capaurl = corsproxy + btoa(capaurl);
          }

          const r = await fetch(capaurl + '?SERVICE=WMS&REQUEST=GetCapabilities&LAYER_ID=' + layer_id);
          const text = await r.text();
          const parser = new ol.format.WMSCapabilities();
          const caps = parser.read(text);
          const getMapUrl: string = caps.Capability.Request.GetMap.DCPType[0].HTTP.Get.OnlineResource;
          if (getMapUrl) {
            if (corsproxy) {
              url = corsproxy + btoa(getMapUrl);
            } else {
              url = getMapUrl;
            }
          }
        } catch (err) {
          console.error(err);
        }
      }
      if (forceBasicAuth) {
        params.forceBasicAuth = forceBasicAuth;
      }

      options.source = new ol.source.ImageWMS({
        attributions: layer.copyright,
        url: url,
        projection: layerProjection,
        crossOrigin: layer.crossOrigin,
        ratio: defaultRatio,
        resolutions: defaultResolutions,
        imageLoadFunction: function (image, src) {
          this.loadWmsImage(layer, image, src, layer.loginName, layer.password, !!layer_id);
        }.bind(this),
        params: params,
      });

      options.source.layerdefinition = layer;
      ollayer = new ol.layer.Image(options);
    } else if (layer.type === MapLayerType[MapLayerType.WMTS]) {
      let url: string;
      let version: string;
      let format: string;
      let corsProxyUrl: string;
      if (layer.dataSource) {
        url = layer.dataSource?.split('?')[0];
        try {
          let fullurl = layer.dataSource;
          const idx = fullurl.indexOf('/CorsProxy/');
          if (idx >= 0) {
            corsProxyUrl = fullurl.substring(0, idx + '/CorsProxy/'.length);
            const base64 = fullurl.substring(idx + '/CorsProxy/'.length);
            fullurl = atob(base64);
          }
          const urlObject = new URL(fullurl);
          version = urlObject.searchParams.get('version') ?? urlObject.searchParams.get('VERSION');
          format = urlObject.searchParams.get('format') ?? urlObject.searchParams.get('FORMAT');
        } catch {}
      }

      try {
        let wmtsCap = undefined;
        try {
          wmtsCap = await this.loadWmtsCapabilities(layer, url, layer.loginName, layer.password);
        } catch (error) {
          console.error('An error occurred while loading WMTS capabilities:', error);
        }

        if (layer.matrixSetName && layerProjection && layerProjection.getCode && layerProjection.getCode() && wmtsCap) {
          const wmtsMatrixSets = wmtsCap['Contents']['TileMatrixSet'];
          const wmtsMatrixSet = wmtsMatrixSets.find(function (elt) {
            return elt['Identifier'] == layer.matrixSetName;
          });
          if (wmtsMatrixSet) {
            wmtsMatrixSet['SupportedCRS'] = layerProjection.getCode();
          }
        }

        if (wmtsCap) {
          const sourceoptions: any = ol.source.WMTS.optionsFromCapabilities(wmtsCap ?? {}, {
            layer: layer.layerName,
            matrixSet: layer.matrixSetName,
            style: layer.styleName,
            projection: layerProjection,
            format: format,
            crossOrigin: layer.crossOrigin,
          });
          if (sourceoptions) {
            if (layer.copyright) {
              sourceoptions.attributions = layer.copyright;
            }

            sourceoptions.tileLoadFunction = function (imageTile, src) {
              if (corsProxyUrl) {
                src = corsProxyUrl + btoa(src);
              }
              this.loadWmtsImageTile(layer, imageTile, src, layer.loginName, layer.password);
            }.bind(this);

            if (version) {
              sourceoptions.version = version;
            }

            options.source = new ol.source.WMTS(sourceoptions);

            options.source.layerdefinition = layer;
          }
        }
        ollayer = new ol.layer.Tile(options);
      } catch (error) {
        console.error('An error occurred while loading WMTS capabilities:', error);
      }
    } else if (layer.type === MapLayerType[MapLayerType.XYZ]) {
      this.enforceDefaultViewProjection();
      if (layer.url) {
        const url = layer.url;
        options.source = new ol.source.XYZ({
          attributions: layer.copyright,
          url: url,
          tileLoadFunction: function (imageTile, src) {
            imageTile.getImage().src = src;
          }.bind(this),
          minZoom: layer.minZoom,
          maxZoom: layer.maxZoom,
          transition: DotNetBridge.hasContext ? 0 : 250,
          crossOrigin: layer.crossOrigin,
        });
        options.source.layerdefinition = layer;

        ollayer = new ol.layer.Tile(options);
      } else {
        let url: string;
        if (layer.webApiUrl) {
          url = layer.webApiUrl + '/api/Maps/ReadStaticMapTile?{z}/{y}/{x}';
        } else {
          url = this.readMapImageUrl.replace('ReadMapImage', 'ReadMapTile?{z}/{y}/{x}');
        }
        options.source = new ol.source.VectorTile({
          attributions: layer.copyright,
          format: this.mvtFormat,
          url: url,
          tileLoadFunction: function (tile, url) {
            tile.setLoader(
              function (extent, resolution, projection) {
                this.loadXYZVectorTile(
                  layer,
                  name,
                  tile,
                  url,
                  layer.webApiTilesName,
                  layer.dataSource,
                  extent,
                  layerProjection,
                );
              }.bind(this),
            );
          }.bind(this),
          minZoom: layer.minZoom,
          maxZoom: layer.maxZoom,
          transition: DotNetBridge.hasContext ? 0 : 250,
        });
        options.source.layerdefinition = layer;
        this.tileCacheSources.set(name, options.source);

        if (DotNetBridge.hasContext) {
          options.updateWhileAnimating = true;
          options.updateWhileInteracting = true;
          options.renderMode = 'vector';
        }

        var osmStyle = new MapOsmStyle(this.coreDataService);
        options.style = function (feature, resolution) {
          const mapstyles = osmStyle.findStyles(feature, resolution);
          const styles = [];
          for (const mapstyle of mapstyles) {
            styles.push(...this.createStyle(feature, resolution, mapstyle, layer.minScaleSizing, layer.maxScaleSizing));
          }
          return styles;
        }.bind(this);

        ollayer = new ol.layer.VectorTile(options);
      }
    } else if (layer.type === MapLayerType[MapLayerType.Shp] || layer.type === MapLayerType[MapLayerType.Dbms]) {
      const strategy = ol.loadingstrategy.bbox;

      if (minScale != null && layer.minScale <= minScale) options.minResolution = undefined;

      const partitionColumnNames: string[] = [];
      if (layer.partitionColumnName) {
        if (typeof layer.partitionColumnName === 'string') {
          partitionColumnNames.push(layer.partitionColumnName);
        } else {
          partitionColumnNames.push(...layer.partitionColumnName);
        }
      }
      const tooltipColumnNames: string[] = [];
      const expressionColumnNames: string[] = [];
      expressionColumnNames.push(...partitionColumnNames);
      if (layer.columns) {
        for (let c of layer.columns) {
          if (c.useForTooltip && tooltipColumnNames.indexOf(c.name) < 0) {
            tooltipColumnNames.push(c.name);
          } else if (c.useForExpression && expressionColumnNames.indexOf(c.name) < 0) {
            expressionColumnNames.push(c.name);
          }
        }
      }
      if (layer.identityColumnName && !expressionColumnNames.find((_) => _ === layer.identityColumnName)) {
        expressionColumnNames.push(layer.identityColumnName);
      }
      const keyExternColumnName = 'KeyExtern';
      if (
        layer.columns?.find((_) => _.name === keyExternColumnName) &&
        !expressionColumnNames.find((_) => _ === keyExternColumnName)
      ) {
        expressionColumnNames.push(keyExternColumnName);
      }

      const shpLayerProjection = layerProjection;
      const loader = (extent: number[], resolution: number, projection: any) => {
        return this.loadShpFeatures(
          layer,
          src,
          layer.dataSource,
          layer.objectClassName,
          layer.filter,
          expressionColumnNames,
          tooltipColumnNames,
          extent,
          shpLayerProjection,
          projection,
        );
      };

      const src = new ol.source.Vector({
        attributions: layer.copyright,
        loader: loader,
        strategy: strategy,
        projection: layerProjection,
      });

      src.loader = loader;

      options.source = src;
      options.source.layerdefinition = layer;
      const vectorStyle = new MapVectorStyle(layer.style, partitionColumnNames);
      options.style = this.createStylesFunc(layer, useLabelPlacing, 'default', (feature, resolution) => {
        const mapstyles = vectorStyle.findStyles(feature, resolution);
        const styles: Array<{ mapStyle: MapStyle; style: any }> = [];
        for (const mapstyle of mapstyles) {
          for (const style of this.createStyle(feature, resolution, mapstyle)) {
            styles.push({ mapStyle: mapstyle, style: style });
          }
        }
        return styles;
      });

      if (imageVectorScale != null && layer.maxScale >= imageVectorScale) {
        options.renderMode = 'image';
      }

      ollayer = new ol.layer.Vector(options);
    } else if (layer.type === MapLayerType[MapLayerType.Raster]) {
      options.source = new ol.source.ImageMapGuide({
        attributions: layer.copyright,
        url: this.readMapImageUrl,
        projection: layerProjection,
        crossOrigin: layer.crossOrigin,
        resolutions: defaultResolutions,
        imageLoadFunction: function (image, src) {
          this.loadRasterImage(
            layer,
            image,
            src,
            layer.dataSource,
            layer.objectClassName,
            layer.layerNames,
            layerProjection,
          );
        }.bind(this),
      });

      options.source.layerdefinition = layer;
      ollayer = new ol.layer.Image(options);
    } else if (layer.type === MapLayerType[MapLayerType.GeoTIFF]) {
      options.source = new ol.source.GeoTIFF({
        attributions: layer.copyright,
        sources: [
          {
            projection: layerProjection,
            url: layer.url,
          },
        ],
      });

      options.source.layerdefinition = layer;
      ollayer = new ol.layer.WebGLTile(options);
    } else if (layer.type === MapLayerType[MapLayerType.ObjectSpace]) {
      const strategy = ol.loadingstrategy.bbox;

      if (minScale != null && layer.minScale <= minScale) options.minResolution = undefined;

      const vectorSource = new ol.source.Vector({
        attributions: layer.copyright,
        loader: (extent: number[], resolution: number, projection: any) => {
          this.loadObjectSpaceFeatures(layer, name, vectorSource, layer.query, layerProjection, extent, projection);
        },
        strategy: strategy,
        projection: layerProjection,
      });

      //layerdefinition references to the model layer of basys.
      vectorSource.layerdefinition = layer;
      options.source = vectorSource;
      options.style = this.createStylesFunc(layer, useLabelPlacing, undefined, (feature, resolution) =>
        this.createStyles(layer, feature, resolution),
      );

      if (imageVectorScale != null && layer.maxScale >= imageVectorScale) {
        options.renderMode = 'image';
      }

      ollayer = new ol.layer.Vector(options);
      this.queryCacheSources.set(name, options.source);
    }
    if (ollayer) {
      ollayer.layerdefinition = layer;
      ollayer.fullName = name;
    }

    return ollayer;
  }

  private createStylesFunc(
    layer: MapLayer,
    useLabelPlacing: boolean,
    defaultGroupKey: string,
    stylesFactory: (feature: any, resolution: number) => { mapStyle: MapStyle; style: any }[],
  ): (feature: any, resolution: number) => any[] {
    return (feature, resolution) => {
      const styles = stylesFactory(feature, resolution);
      const normalStyles: { mapStyle: MapStyle; style: any }[] = [];
      const textStyles: { mapStyle: MapStyle; style: any }[] = [];
      for (const e of styles) {
        const mapStyle = e.mapStyle;
        const style = e.style;
        if (useLabelPlacing && this.useLabelPlacing(mapStyle) && style.getText()) {
          textStyles.push(e);
        } else {
          normalStyles.push(e);
        }
      }
      if (useLabelPlacing) {
        const center = this.olmap.getView().getCenter();
        const placer = MapLabelPlacer.get(center, resolution, defaultGroupKey);
        this.lastMapLabelPlacer = placer;
        const newTextStyles = placer.append(
          feature,
          textStyles,
          normalStyles,
          (geom) => this.transformGeometryToData(geom),
          (mapStyle) => this.prepareResolution(resolution, layer.minScaleSizing, layer.maxScaleSizing, mapStyle),
        );
        if (newTextStyles) {
          normalStyles.push(...newTextStyles);
        }
      }
      return normalStyles.map((_) => _.style);
    };
  }

  private useLabelPlacing(mapStyle: MapStyle): boolean {
    return (
      !!mapStyle?.text?.declutterGroupColumnName ||
      !!mapStyle.text?.declutterCategoryName ||
      !!mapStyle.text?.declutterMaxDistance ||
      !!mapStyle.text?.declutterMinScale
    );
  }

  private transformGeometryToData(geom: any): any {
    if (this.dataViewProjection && this.enforcedViewProjection) {
      geom = geom.clone().transform(this.enforcedViewProjection, this.dataViewProjection);
    }
    return geom;
  }

  private loadWmsImage(
    layer: MapLayer,
    image: any,
    src: string,
    loginName: string,
    password: string,
    useFetch: boolean,
  ): void {
    var client = new XMLHttpRequest();
    const url = new URL(src);
    let displaywidth = Number.parseFloat(url.searchParams.get('WIDTH'));
    let displayheight = Number.parseFloat(url.searchParams.get('HEIGHT'));
    if (this.renderMapDpi) {
      url.searchParams.set('MAP_RESOLUTION', this.renderMapDpi.toFixed(0));
      url.searchParams.set('DPI', this.renderMapDpi.toFixed(0));
      url.searchParams.set('FORMAT_OPTIONS', 'dpi:' + this.renderMapDpi.toFixed(0));
      src = url.toString();
    }
    client.open('GET', src);
    if (loginName && loginName != '' && password && password != '') {
      client.setRequestHeader('Authorization', 'Basic ' + btoa(loginName + ':' + password));
    }

    const maxPixel = 4000;
    if (displaywidth > maxPixel || displayheight > maxPixel) {
      const adjustFactor = displaywidth > displayheight ? maxPixel / displaywidth : maxPixel / displayheight;
      displaywidth = Math.round(displaywidth * adjustFactor);
      displayheight = Math.round(displayheight * adjustFactor);
      url.searchParams.set('WIDTH', displaywidth.toFixed(0));
      url.searchParams.set('HEIGHT', displayheight.toFixed(0));

      this.loadWmsImage(layer, image, url.toString(), loginName, password, useFetch);
      return;
    }
    client.responseType = 'blob';
    const ml = this;
    const loadImage = (response: any) => {
      const reader = new FileReader();
      reader.onloadend = function () {
        const base64data = reader.result as string;
        if (!(base64data?.indexOf('image') >= 0) && (displaywidth > 1920 || displayheight > 1920)) {
          url.searchParams.set('WIDTH', (displaywidth / 2.0).toFixed(0));
          url.searchParams.set('HEIGHT', (displayheight / 2.0).toFixed(0));

          ml.loadWmsImage(layer, image, url.toString(), loginName, password, useFetch);
        } else {
          image.getImage().src = base64data;
        }
      };
      reader.readAsDataURL(response);
    };
    client.onloadend = function () {
      if (this.response?.size) {
        loadImage(this.response);
      }
    };
    client.onerror = function () {
      ml.addLayerLoadingErrorNotification(layer);
    };

    if (!useFetch) {
      client.send();
    } else {
      fetch(src, { credentials: 'include' }).then(
        async (r) => {
          loadImage(await r.blob());
        },
        (err) => {
          ml.addLayerLoadingErrorNotification(layer);
        },
      );
    }
  }

  private loadWmtsImageTile(layer: MapLayer, imageTile: any, src: string, loginName: string, password: string): void {
    var client = new XMLHttpRequest();
    client.open('GET', src);
    if (loginName && loginName != '' && password && password != '') {
      client.setRequestHeader('Authorization', 'Basic ' + btoa(loginName + ':' + password));
    }

    client.responseType = 'blob';
    const ml = this;
    client.onloadend = function () {
      if (this.response?.size) {
        const reader = new FileReader();
        reader.onloadend = function () {
          const base64data = reader.result as string;
          imageTile.getImage().src = base64data;
        };
        reader.readAsDataURL(this.response);
      }
    };

    client.send();
  }

  private loadWmtsCapabilities(layer: MapLayer, src: string, loginName: string, password: string): Promise<any> {
    var client = new XMLHttpRequest();
    client.open('GET', src);
    if (loginName && loginName != '' && password && password != '') {
      client.setRequestHeader('Authorization', 'Basic ' + btoa(loginName + ':' + password));
    }

    client.responseType = 'text';
    const subject = new Subject<any>();
    client.onloadend = function () {
      const parser = new ol.format.WMTSCapabilities();
      subject.next(parser.read(this.response));
      subject.complete();
    };

    client.send();

    return subject.asObservable().toPromise();
  }

  private loadXYZVectorTile(
    layer: MapLayer,
    name: string,
    tile: any,
    src: string,
    tilesName: string,
    dataSource: string,
    extent: number[],
    olprojection: any,
  ): void {
    let loadVectorTile: Observable<any>;
    if (CacheStorageAdapter.useOfflineMode) {
      loadVectorTile = this.loadVectorTileFromCache(name, tile.tileCoord);
    } else {
      loadVectorTile = this.loadVectorTile(tilesName, dataSource, src, tile.tileCoord).pipe(
        catchError((_) => {
          return this.loadVectorTileFromCache(name, tile.tileCoord);
        }),
      );
    }
    loadVectorTile.subscribe(
      (_) => {
        const format = tile.getFormat(); // ol/format/MVT configured as source format
        const features = format.readFeatures(_, {
          extent: extent,
        });
        if (features?.length > 0) {
          tile.setFeatures(features);
        }
      },
      (error) => {
        console.log(error);
        this.addLayerLoadingErrorNotification(layer);
      },
    );
  }

  public loadVectorTile(
    tilesName: string,
    dataSource: string,
    src: string,
    tileCoord: number[],
  ): Observable<ArrayBuffer> {
    const url = new URL(src);
    let data;
    if (tilesName) {
      data = {
        Name: tilesName,
        ZoomLevel: tileCoord[0],
        X: tileCoord[1],
        Y: tileCoord[2],
      };
    } else {
      data = {
        PackageId: this.packageId,
        MapNames: this.mapNames,
        DataSource: dataSource,
        ZoomLevel: tileCoord[0],
        X: tileCoord[1],
        Y: tileCoord[2],
      };
    }
    return this.coreDataService
      .executeWebApiPOSTMethodBlob(url.origin + url.pathname, data)
      .pipe(concatMap((blob) => from(blob.arrayBuffer())));
  }

  private loadVectorTileFromCache(name: string, tileCoord: number[]): Observable<any> {
    if (this.tileCache) {
      return from(this.tileCache.tryLoadTile(name, tileCoord));
    } else {
      return of(null);
    }
  }

  private loadRasterImage(
    layer: MapLayer,
    image: any,
    src: string,
    dataSource: string,
    objectClassName: string,
    layerNames: string[],
    olprojection: any,
  ): void {
    const url = new URL(src);
    let displaywidth = Number.parseFloat(url.searchParams.get('SETDISPLAYWIDTH'));
    let displayheight = Number.parseFloat(url.searchParams.get('SETDISPLAYHEIGHT'));
    const maxPixel = 4000 * 4000;
    if (displaywidth * displayheight > maxPixel) {
      const adjustFactor = maxPixel / (displaywidth * displayheight);
      displaywidth = Math.round(displaywidth * adjustFactor);
      displayheight = Math.round(displayheight * adjustFactor);
    }

    const size = this.olmap.getSize();
    const view = this.olmap.getView();
    const viewprojection = view.getProjection();
    let extent = view.calculateExtent(size);
    if (viewprojection && olprojection && extent?.length === 4) {
      const pmin = ol.proj.transform([extent[0], extent[1]], viewprojection, olprojection);
      const pmax = ol.proj.transform([extent[2], extent[3]], viewprojection, olprojection);

      extent = [pmin[0], pmin[1], pmax[0], pmax[1]];
    }

    const data = {
      PackageId: this.packageId,
      MapNames: this.mapNames,
      DataSource: dataSource,
      ObjectClassName: objectClassName,
      LayerNames: layerNames,
      Viewbox: extent,
      ImageWidth: displaywidth,
      ImageHeight: displayheight,
    };

    const ml = this;
    this.coreDataService.executeWebApiPOSTMethodBlob(url.origin + url.pathname, data).subscribe(
      (img) => {
        if (img?.size) {
          const reader = new FileReader();
          reader.onload = function () {
            const base64data = reader.result;
            image.getImage().src = base64data;
          };
          reader.onerror = function () {
            console.log(reader.error);
            ml.addLayerLoadingErrorNotification(layer);
          };
          reader.readAsDataURL(img);
        } else {
          ml.addLayerLoadingErrorNotification(layer);
        }
      },
      (error) => {
        console.log(error);
        ml.addLayerLoadingErrorNotification(layer);
      },
    );
  }

  private addLayerLoadingErrorNotification(layer: MapLayer, that?: MapLoader) {
    this.layersWithLoadErrors.push(layer);
    this.olmap.updateSize();
  }

  private createLayerOptions(layer: MapLayer, ignoreScales: boolean): any {
    const options = new ol.Object();

    if (layer.name != null) options.name = layer.name;
    if (layer.displayText != null && layer.displayText !== '') options.name = layer.displayText;
    // visible is also handled in legend!
    if (ignoreScales) {
      options.visible = true;
    } else if (layer.visibleInLegend !== false) {
      options.visible = false;
    } else if (layer.visible != null) {
      options.visible = layer.visible;
    }
    if (!ignoreScales) {
      if (layer.minScale != null) options.minResolution = MapLoader.getResolutionFromScale(layer.minScale);
      if (layer.maxScale != null) options.maxResolution = MapLoader.getResolutionFromScale(layer.maxScale);
    }
    if (layer.opacity != null) options.opacity = layer.opacity;
    if (layer.zIndex != null) options.zIndex = layer.zIndex;

    return options;
  }

  private getSizingResolution(resolution: number, minScaleSizing: number, maxScaleSizing: number) {
    if (minScaleSizing > 0.0 && MapLoader.getScaleFromResolution(resolution) < minScaleSizing) {
      resolution = MapLoader.getResolutionFromScale(minScaleSizing);
    } else if (maxScaleSizing > 0.0 && MapLoader.getScaleFromResolution(resolution) > maxScaleSizing) {
      resolution = MapLoader.getResolutionFromScale(maxScaleSizing);
    }
    return resolution;
  }

  private prepareResolution(
    resolution: number,
    minScaleSizing: number,
    maxScaleSizing: number,
    style: MapStyle,
  ): number {
    resolution = this.getSizingResolution(resolution, minScaleSizing, maxScaleSizing);
    resolution = this.getSizingResolution(resolution, style.minScaleSizing, style.maxScaleSizing);

    if (this.resolutionFactor) {
      resolution *= this.resolutionFactor;
    }
    return resolution;
  }

  private createStyles(layer: MapLayer, feature: any, resolution: number): { mapStyle: MapStyle; style: any }[] {
    const styles: { mapStyle: MapStyle; style: any }[] = [];
    let partition = null;
    if (layer.partitionColumnName != null && layer.partitionColumnName !== '')
      partition = feature.get(layer.partitionColumnName);

    for (const style of layer.style) {
      if (style.partition === partition || (styles.length === 0 && partition != null && style.partition == null)) {
        for (const s of this.createStyle(feature, resolution, style, layer.minScaleSizing, layer.maxScaleSizing)) {
          styles.push({ mapStyle: style, style: s });
        }
      }
    }
    return styles;
  }

  private createStyle(
    feature: any,
    resolution: number,
    style: MapStyle,
    minScaleSizing?: number,
    maxScaleSizing?: number,
  ): any[] {
    resolution = this.prepareResolution(resolution, minScaleSizing, maxScaleSizing, style);

    const options = new ol.Object();
    if (style.zIndex != null) options.zIndex = style.zIndex;

    if (this.tryGetFeatureSelectionColor(feature)) {
      options.zIndex = Infinity;
    }

    let strokeColor: number[] = null;
    const additionalStyleOptions: object[] = [];
    if (style.stroke != null) {
      options.stroke = this.createStroke(feature, resolution, style.stroke, additionalStyleOptions);
      if (options.stroke != null) strokeColor = options.stroke.getColor();
    }

    if (style.fill != null) options.fill = this.createFill(feature, resolution, style.fill, strokeColor);

    if (style.image != null) options.image = this.createImage(feature, resolution, style.image, style.stroke);

    let result: any[] = null;
    if (style.text != null) {
      const texts = this.createTexts(feature, resolution, style.text);
      if (texts?.length) {
        options.text = texts[0];
        result = [];
        result.push(new ol.style.Style(options));
        if (texts.length > 1) {
          for (let i = 1; i < texts.length; i++) {
            const textoptions = new ol.Object();
            if (style.zIndex != null) textoptions.zIndex = style.zIndex;
            textoptions.text = texts[i];
            result.push(new ol.style.Style(textoptions));
          }
        }
      }
    }

    if (!result) {
      result = [new ol.style.Style(options)];
    }
    if (additionalStyleOptions) {
      for (const o of additionalStyleOptions) {
        if (style.zIndex != null) o['zIndex'] = style.zIndex;
        result.push(new ol.style.Style(o));
      }
    }
    return result;
  }

  private createFill(feature: any, resolution: number, fill: MapStyleFill, strokeColor: number[]): any {
    const options = new ol.Object();
    if (fill.color != null || fill.isInvisible)
      options.color = this.getColor(feature, fill.color, fill.isInvisible, resolution, strokeColor);

    return new ol.style.Fill(options);
  }

  private iconsCache: Map<string, any> = new Map();
  private createImage(feature: any, resolution: number, image: MapStyleImage, stroke: MapStyleStroke): any {
    const options: any = {};
    let factor = 1.0;
    if (image.color != null || image.isInvisible != null)
      options.color = this.getColor(feature, image.color, image.isInvisible, resolution);
    let scale = 1.0;
    if (image.scale != null) {
      scale = this.getValue(feature, image.scale) as any as number;
    }
    if (image.src != null) {
      const o = this.getValue(feature, image.src) as any;
      if (typeof o === 'object') {
        options.src = o.src;
        if (o.imgSize) {
          options.imgSize = o.imgSize;
        }
        if (o.imgFactor) {
          scale *= o.imgFactor;
        }
      } else {
        options.src = o;
      }
      const strokewidthname = 'stroke-width:';
      const strokewidthdefaultvalue = 0.05;
      const strokewidthdefaultvalue2 = 0.049;
      const strokewidthexpression = strokewidthname + strokewidthdefaultvalue;
      const strokewidthexpression2 = strokewidthname + strokewidthdefaultvalue2;
      let src: string = options.src;
      if (scale > 0.0 && scale != 1.0 && src) {
        let divscale = scale;
        if (divscale > 1.01) {
          divscale = 1.0 + (divscale - 1.0) / 3;
        }
        const strokewidthscaled = strokewidthdefaultvalue / divscale;
        src = this.decodeSvgFromBase64(src);
        src = src.split(strokewidthexpression).join(strokewidthname + strokewidthscaled);
        src = src.split(strokewidthexpression2).join(strokewidthname + strokewidthscaled);
        options.src = src;
      }

      let width: any = null;
      if (stroke?.width != null) {
        width = this.getValue(feature, stroke.width);
      }

      if (src) {
        const origsrc = src;
        src = this.prepareSvgWithStrokeWidth(src, width > 0.0 ? width : strokewidthdefaultvalue / 2.0);
        if (origsrc !== src) {
          options.src = src;
        }
      }
      if (options.color?.length >= 3 && options.color !== this.selectionColor && src) {
        const origsrc = src;
        src = this.prepareSvgWithColor(src, options.color);
        if (origsrc !== src) {
          options.src = src;
          options.color = undefined;
        }
      }
      if (options.imgSize && options.imgSize.length > 1 && options.imgSize[1] !== 0.0) {
        factor /= options.imgSize[1];
      }
    }
    if (image.imgSize != null) {
      options.imgSize = this.getValue(feature, image.imgSize);
      if (options.imgSize && options.imgSize.length > 1 && options.imgSize[1] !== 0.0) {
        factor /= options.imgSize[1];
      }
    }
    if (image.imgFactor) {
      scale *= this.getValue(feature, image.imgFactor) as any as number;
    }

    options.scale = scale * factor * MapLoader.getSizeFactor(resolution);

    if (image.anchor != null) options.anchor = this.getValue(feature, image.anchor);
    if (image.anchorOrigin != null) options.anchorOrigin = this.getValue(feature, image.anchorOrigin);
    if (image.anchorXUnits != null) options.anchorXUnits = this.getValue(feature, image.anchorXUnits);
    if (image.anchorYUnits != null) options.anchorYUnits = this.getValue(feature, image.anchorYUnits);
    if (image.crossOrigin != null) options.crossOrigin = this.getValue(feature, image.crossOrigin);
    if (image.displacement != null) options.displacement = this.getValue(feature, image.displacement);
    if (image.offset != null) options.offset = this.getValue(feature, image.offset);
    if (image.offsetOrigin != null) options.offsetOrigin = this.getValue(feature, image.offsetOrigin);
    if (image.opacity != null) options.opacity = this.getValue(feature, image.opacity);
    if (image.rotateWithView != null) options.rotateWithView = this.getValue(feature, image.rotateWithView);
    if (image.rotation != null)
      options.rotation = MapLoader.normalizeRotation(this.getValue(feature, image.rotation, -1));

    if (image.size != null) options.size = this.getValue(feature, image.size);
    if (image.snapToPixel != null) options.snapToPixel = this.getValue(feature, image.snapToPixel);

    if (image.offsetX || image.offsetY) {
      const imagemeters: number = scale;

      let x = 0.5;
      let y = 0.5;
      if (options.anchor?.length > 0) x = options.anchor[0];
      if (options.anchor?.length > 1) y = options.anchor[1];

      options.anchor = [
        x - ((this.getValue(feature, image.offsetX, 1.0 / imagemeters) as any as number) ?? 0.0),
        y + ((this.getValue(feature, image.offsetY, 1.0 / imagemeters) as any as number) ?? 0.0),
      ];
      options.anchorXUnits = 'fraction';
      options.anchorYUnits = 'fraction';
    }

    const imgSize: [number, number] = options.imgSize;
    options.imgSize = null;

    if (!options.src) {
      return null;
    }
    if (!options.color) {
      options.color = undefined;
    }
    let icon = this.getIconFromOptions(options);
    icon.imgSize = imgSize;
    return icon;
  }

  private prepareSvgWithColor(svg: string, color: number[]): string {
    if (color?.length > 2 && svg) {
      const origsvg = svg;
      const decodedsvg = this.decodeSvgFromBase64(svg);
      svg = decodedsvg;
      const colorstring = 'rgb(' + color[0] + ',' + color[1] + ',' + color[2] + ')';
      svg = svg.replace(/param\(outline\).*?(?=;|"|$)/g, colorstring);
      svg = svg.replace(/param\(fill\).*?(?=;|"|$)/g, colorstring);
      if (svg === decodedsvg) {
        svg = origsvg;
      }
    }
    return svg;
  }

  private prepareSvgWithStrokeWidth(svg: string, width: number | ''): string {
    if ((width === '' || width > 0.0) && svg) {
      const origsvg = svg;
      const decodedsvg = this.decodeSvgFromBase64(svg);
      svg = decodedsvg;
      const widthstring: string = width.toString();
      svg = svg.replace(/param\(outline-width\).*?(?=;|"|$)/g, widthstring);
      if (svg === decodedsvg) {
        svg = origsvg;
      }
    }
    return svg;
  }

  private decodeSvgFromBase64(svg: string) {
    const arr = svg.split(',');
    if (arr.length > 1 && arr[0].endsWith(';base64')) {
      arr[0] = arr[0].replace(';base64', ';charset=utf-8');
      arr[1] = atob(arr[1]).replace(/[\%\ \#\&]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
      svg = arr.join(',');
    }
    return svg;
  }

  private getIconFromOptions(options: any): any {
    const key: string = JSON.stringify(options);
    let icon = this.iconsCache.get(key);
    if (!icon) {
      if (this.iconsCache.size > 1000) {
        this.iconsCache.clear();
      }
      const options_ = new ol.Object();
      Object.assign(options_, options);
      icon = new ol.style.Icon(options_);
      this.iconsCache.set(key, icon);
    }
    return icon;
  }

  private createStroke(
    feature: any,
    resolution: number,
    stroke: MapStyleStroke,
    additionalStyleOptions: object[] = null,
  ): any {
    const options = new ol.Object();
    if (stroke.color != null) options.color = this.getColor(feature, stroke.color, stroke.isInvisible, resolution);
    if (stroke.width != null) {
      let width = this.getValue(feature, stroke.width, MapLoader.getSizeFactor(resolution));
      if ((width as any) < 1.0) {
        width = 1.0 as any;
      }
      options.width = width;
    }
    if (stroke.lineCap != null) options.lineCap = this.getValue(feature, stroke.lineCap);
    if (stroke.lineDash != null) {
      let value = this.getValue(feature, stroke.lineDash);
      if (value && !Array.isArray(value)) {
        const v = ObjectHelper.cloneDeep(
          value as { dashsAndGaps: number[]; shapeIdentifiers: string[]; transparency: number },
        );
        const shapeIdentifiers = v.shapeIdentifiers;
        if (v.dashsAndGaps?.length && shapeIdentifiers?.length) {
          let dashsAndGaps = ObjectHelper.cloneArray(v.dashsAndGaps);
          const foundIndexes: number[] = [];
          const shapes: Map<string, number[]> = new Map();
          for (const shapeIdentifier of CollectionHelper.distinctValues(shapeIdentifiers.filter((_) => _))) {
            const placement: number[] = [];
            let k = 0;
            let pos = 0.0;
            for (let i = 1; i < dashsAndGaps.length; i += 2) {
              pos += dashsAndGaps[i - 1] + dashsAndGaps[i];
              if (k < shapeIdentifiers.length && shapeIdentifier === shapeIdentifiers[k++]) {
                placement.push(pos);
                foundIndexes.push(i);
              }
            }
            shapes.set(shapeIdentifier, placement);
          }
          for (let i of foundIndexes) {
            dashsAndGaps[i] += dashsAndGaps[i - 1];
            dashsAndGaps[i - 1] = 0.0;
          }
          shapes.set('', [CollectionHelper.sum(dashsAndGaps)]);
          if (
            !shapes.size ||
            (shapes.size === 1 &&
              ((dashsAndGaps.length > 0 && dashsAndGaps[0] !== 0.0 && dashsAndGaps.length < 2) ||
                (dashsAndGaps.length === 2 && dashsAndGaps[1] === 0.0)))
          ) {
            v.dashsAndGaps = dashsAndGaps;
          } else if (additionalStyleOptions) {
            v.transparency = 1.0;
            const geom = feature.getGeometry();
            let coordinates = geom?.getCoordinates();
            while (coordinates?.length && Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0])) {
              coordinates = coordinates[0];
            }
            if (coordinates?.length) {
              const ls = new ol.geom.LineString(coordinates, geom.getLayout());
              const lslength = ls.getLength();
              const symbolWidth = 1.0;
              const symbolLineWidth = 0.05;
              let lineWidth = options.width / MapLoader.getSizeFactor(resolution);
              if (!lineWidth) {
                lineWidth = 0.05;
              }
              for (const [shapeIdentifier, placement] of shapes) {
                const stylingLength = shapes.get('')[0];
                if (stylingLength) {
                  const count = Math.floor(lslength / stylingLength);
                  const offset = (lslength - count * stylingLength) / 2.0;
                  for (let i = 0; i < count; i++) {
                    const startPos = offset + i * stylingLength;
                    const startFraction = i / count;
                    const stylingFraction = stylingLength / (count * stylingLength);
                    for (const pos of CompareHelper.isNullorEmpty(shapeIdentifier) && i == 0 && placement.length === 1
                      ? [0, placement[0]]
                      : placement) {
                      const fullpos = startPos + pos;
                      let rotation: number = undefined;
                      let currentpos: number = 0.0;
                      ls.forEachSegment((start, end) => {
                        const x = end[0] - start[0];
                        const y = end[1] - start[1];
                        currentpos += Math.sqrt(x * x + y * y);
                        if (currentpos >= fullpos && rotation === undefined) {
                          rotation = -Math.atan2(y, x) + Math.PI / 2.0;
                          return true;
                        }
                      });
                      const fraction = startFraction + (pos / stylingLength) * stylingFraction;
                      const coordinate = ls.getCoordinateAt(fraction);
                      const svg = this.getSvgShape(shapeIdentifier, symbolWidth, symbolLineWidth, lineWidth);
                      const boxWidth = symbolWidth + symbolLineWidth;
                      const fillstyle = svg.nofill ? '#ffffffff' : 'param(fill)';
                      const fullsvg = `<svg width='100' height='100' version='1.1' viewBox='${-boxWidth / 2} ${-boxWidth / 2} ${boxWidth} ${boxWidth}' xmlns='http://www.w3.org/2000/svg' style="stroke-width:${svg.strokewidth};stroke:param(outline);fill:${fillstyle}">${svg.xml}</svg>`;
                      const styleoptions: object = {
                        image: this.getIconFromOptions({
                          src: `data:image/svg+xml;base64,${btoa(this.prepareSvgWithColor(fullsvg, options.color))}`,
                          scale: options.width / 100,
                          rotation: rotation,
                        }),
                        geometry: (feature) => new ol.geom.Point(coordinate),
                      };
                      additionalStyleOptions.push(styleoptions);
                    }
                  }
                }
              }
            }
          }
        }

        if (options.color?.length > 2 && v.transparency) {
          if (options.color.length === 3) {
            options.color = [options.color[0], options.color[1], options.color[2], 1.0];
          }
          options.color[3] *= 1.0 - v.transparency;
        }

        value = v.dashsAndGaps;
      }
      options.lineDash = this.applyFactor(value, MapLoader.getSizeFactor(resolution));
    }
    if (stroke.lineDashOffset != null) {
      options.lineDashOffset = this.getValue(feature, stroke.lineDashOffset, MapLoader.getSizeFactor(resolution));
    }
    if (stroke.lineJoin != null) {
      options.lineJoin = this.getValue(feature, stroke.lineJoin);
    }
    if (stroke.miterLimit != null) {
      options.miterLimit = this.getValue(feature, stroke.miterLimit, MapLoader.getSizeFactor(resolution));
    }

    return new ol.style.Stroke(options);
  }

  private getSvgShape(
    shapeIdentifier: string,
    symbolWidth: number,
    symbolLineWidth: number,
    lineWidth: number,
  ): { xml: string; strokewidth: number; nofill: boolean } {
    let radius = symbolWidth / 2.0;
    if (CompareHelper.isNullorEmpty(shapeIdentifier)) {
      symbolLineWidth /= 2.0;
    } else if (shapeIdentifier === 'O' || shapeIdentifier === 'o') {
      if (shapeIdentifier === 'o') {
        radius *= 2.0 / 3.0;
      }
      let cx = 0.0;
      let cy = 0.0;
      return {
        xml: `<circle cx="${cx}" cy="${cy}" r="${radius}" />`,
        strokewidth: symbolLineWidth,
        nofill: true,
      };
    } else if (shapeIdentifier === 'X' || shapeIdentifier === 'x') {
      if (shapeIdentifier === 'x') {
        radius *= 2.0 / 3.0;
      }
      let x1 = -radius;
      let x2 = +radius;
      let y1 = -radius;
      let y2 = +radius;
      return {
        xml: `<line x1="${x1}" x2="${x2}" y1="${y1}" y2="${y2}" /><line x1="${x1}" x2="${x2}" y1="${y2}" y2="${y1}" />`,
        strokewidth: symbolLineWidth,
        nofill: false,
      };
    }
    const cx = -radius;
    const cy = -symbolLineWidth / 2.0;
    return {
      xml: `<rect x="${cx}" y="${cy}" width="${radius * 2.0}" height="${symbolLineWidth}" />`,
      strokewidth: symbolLineWidth,
      nofill: true,
    };
  }

  private Pythagoras(radius: number): number {
    return Math.sqrt(radius * radius + radius * radius);
  }

  private PythagorasReverse(radius: number): number {
    return Math.sqrt((radius * radius) / 2.0);
  }

  private static readonly defaultFontSize = 5;

  private createTexts(feature: any, resolution: number, text: MapStyleText): any[] {
    const result: any[] = [];
    const textValues: any[] = [];
    const fontValues: any[] = [];
    let widthFactor: number = 0.0;
    if (text.text != null) {
      let fontValue = MapLoader.defaultFontSize + 'px ';
      if (text.font != null) {
        const f = this.getValue(feature, text.font);
        if (!(f instanceof String) && ObjectHelper.isObject(f)) {
          if (f['isItalic']) {
            fontValue = 'italic ' + fontValue;
          }
          if (f['isBold']) {
            fontValue = 'bold ' + fontValue;
          }
          widthFactor = f['widthFactor'];
          if (f['familyName']) {
            fontValue += '"' + f['familyName'] + '", ';
          }
        } else if (f != null) {
          fontValue += '"' + f + '", ';
        }
      }
      fontValue += '"Arial", "sans-serif"';

      const textValue = this.getValue(feature, text.text);
      if (typeof textValue === 'string') {
        const s: string = textValue as string;
        const arr = s?.split(/\r?\n/);
        const isStyle =
          arr?.length &&
          (arr[0].indexOf('<UND>') >= 0 ||
            arr[0].indexOf('<und>') >= 0 ||
            arr[0].indexOf('<ITA>') >= 0 ||
            arr[0].indexOf('<ita>') >= 0 ||
            arr[0].indexOf('<BOL>') >= 0 ||
            arr[0].indexOf('<bol>') >= 0);
        if (arr?.length > 1 || isStyle) {
          for (let i = 0; i < arr.length; i++) {
            const lineValues: string[] = [];
            let underlineValues: string[] = null;
            const lineFontValues: string[] = [];
            for (let j = 0; j < arr.length; j++) {
              if (i === j) {
                let lineValue = arr[i];
                let lineFontValue = '';
                let isUnderline = false;
                const tagPattern = /<([^\s>]+)(\s|>)+/;
                let matchResult: RegExpMatchArray;
                do {
                  matchResult = lineValue.match(tagPattern);
                  if (matchResult?.length > 1) {
                    const startTag = matchResult[0];
                    const tagName = matchResult[1]?.toLowerCase();
                    const endTag = '</' + matchResult[1] + '>';
                    if (lineValue.indexOf(endTag) >= 0) {
                      if (tagName === 'und') {
                        isUnderline = true;
                      } else if (tagName === 'bol') {
                        lineFontValue += 'bold ';
                      } else if (tagName === 'ita') {
                        lineFontValue += 'italic ';
                      }
                      lineValue = lineValue.replace(startTag, '').replace(endTag, '');
                    } else {
                      break;
                    }
                  } else {
                    break;
                  }
                } while (true);

                lineValues.push(lineValue);
                lineFontValues.push(lineFontValue);
                if (isUnderline) {
                  underlineValues = [];
                  underlineValues.push(...lineValues);
                  let underlineText = '';
                  for (let k = 0; k < lineValue.length; k++) {
                    underlineText += '_';
                  }
                  underlineValues[underlineValues.length - 1] = underlineText;
                }
              } else {
                lineValues.push('');
                lineFontValues.push('');
                if (underlineValues?.length) {
                  underlineValues.push('');
                }
              }
            }
            textValues.push(lineValues.join('\n'));
            fontValues.push(lineFontValues.join('') + fontValue);
            if (underlineValues?.length) {
              textValues.push(underlineValues.join('\n'));
              fontValues.push(lineFontValues.join('') + fontValue);
            }
          }
        } else {
          textValues.push(textValue);
          fontValues.push(fontValue);
        }
      } else {
        textValues.push(textValue?.toString());
        fontValues.push(fontValue);
      }
    }
    for (let i = 0; i < textValues.length; i++) {
      const textValue = textValues[i];
      const fontValue = fontValues[i];
      const options = new ol.Object();
      let strokeColor: number[] = null;
      if (text.stroke != null) {
        options.stroke = this.createStroke(feature, resolution, text.stroke);
        if (options.stroke != null) strokeColor = options.stroke.getColor();
      }
      if (text.fill != null) options.fill = this.createFill(feature, resolution, text.fill, strokeColor);
      if (fontValue) options.font = fontValue;
      if (text.offsetX != null)
        options.offsetX = this.getValue(feature, text.offsetX, MapLoader.getSizeFactor(resolution));
      if (text.offsetY != null)
        options.offsetY = this.getValue(feature, text.offsetY, -MapLoader.getSizeFactor(resolution));
      if (text.rotateWithView != null) options.rotateWithView = this.getValue(feature, text.rotateWithView);
      if (text.rotation != null)
        options.rotation = MapLoader.normalizeRotation(this.getValue(feature, text.rotation, -1));
      if (text.scale != null || widthFactor) {
        const scaleY: number = this.getValue(
          feature,
          text.scale ?? 1.0,
          MapLoader.getTextSizeFactor(resolution),
        ) as any;
        if (widthFactor) {
          const scaleX = widthFactor * scaleY;
          options.scale = [scaleX, scaleY];
        } else {
          options.scale = scaleY;
        }
      }
      if (textValue) options.text = textValue;
      if (text.textAlign != null) options.textAlign = this.getValue(feature, text.textAlign);
      if (text.textBaseline != null) options.textBaseline = this.getValue(feature, text.textBaseline);

      const currentRotationOfView = this.olmap.getView().getRotation() ?? 0;
      if (currentRotationOfView !== 0.0) {
        let viewRotation = currentRotationOfView + (options.rotation ?? 0.0);
        while (viewRotation < 0) {
          viewRotation += 2 * Math.PI;
        }
        while (viewRotation > 2 * Math.PI) {
          viewRotation -= 2 * Math.PI;
        }
        if (viewRotation >= Math.PI / 2 && viewRotation < (Math.PI * 3) / 2) {
          options.rotation = (options.rotation ?? 0) + Math.PI;
          if (options.textBaseline == 'top') {
            options.textBaseline = 'bottom';
          } else if (options.textBaseline == 'bottom') {
            options.textBaseline = 'top';
          }
          if (options.textAlign == 'left') {
            options.textAlign = 'right';
          } else if (options.textAlign == 'right') {
            options.textAlign = 'left';
          } else if (options.textAlign == 'start') {
            options.textAlign = 'end';
          } else if (options.textAlign == 'end') {
            options.textAlign = 'start';
          }
        }
      }

      try {
        const geo = feature.getGeometry();
        const type: string = geo?.getType();
        const isPolygon: boolean = type?.indexOf('Polygon') >= 0;
        const isLine: boolean = !isPolygon && type?.indexOf('Point') < 0;

        if (isLine || isPolygon) {
          if (isLine) {
            options.placement = 'line';
          }
          options.overflow = true;
          if (options.scale) {
            options.padding = [2, 2, 2, 2];
          }
        }
      } catch (e) {
        //console.debug(e);
      }

      result.push(new ol.style.Text(options));
    }
    return result;
  }

  private getColor(
    feature: any,
    property: MapStyleValue<number[]>,
    isInvisibleProperty: MapStyleValue<boolean>,
    resolution: number,
    strokeColor: number[] = null,
  ): any {
    let color = this.getColorCore(feature, property, isInvisibleProperty, resolution, strokeColor);
    color = this.applyCustomColorWithFeature(color, feature);

    return color;
  }

  private applyCustomColorWithFeature(color: any, feature: any): any {
    const customColor = this.customObjectColors?.get(MapLoader.getFeatureObjectId(feature)) as number[];
    if (customColor?.length >= 3) {
      color = MapLoader.applyCustomColorCore(color, customColor);
    }
    const selectionColor = this.tryGetFeatureSelectionColor(feature);
    if (selectionColor) {
      color = MapLoader.applyCustomColorCore(color, selectionColor);
    }
    return color;
  }

  private tryGetFeatureSelectionColor(feature: any): number[] {
    if (this.selectionColor?.length >= 3 && this.mapSelection?.isSelectedFeature(feature)) {
      return this.selectionColor;
    }
  }

  private static applyCustomColorCore(color: any, customColor: number[]): any {
    if (customColor?.length >= 3) {
      if (color?.length >= 3) {
        const r = customColor[0];
        const g = customColor[1];
        const b = customColor[2];
        let a = 1.0;
        if (customColor.length > 3) {
          a = customColor[3];
        }

        color = ObjectHelper.cloneDeep(color);

        const brightness = (color[0] * 299 + color[1] * 587 + color[2] * 114) / 1000 / 255.0;
        const factor = 0.7 + brightness * 0.3;
        color[0] = factor * r;
        color[1] = factor * g;
        color[2] = factor * b;
        if (color.length > 3 || a < 1.0) {
          color[3] = ((color[3] ?? 1.0) + a) / 2.0;
        }
      } else {
        color = customColor;
      }
    }
    return color;
  }

  private getColorCore(
    feature: any,
    property: MapStyleValue<number[]>,
    isInvisibleProperty: MapStyleValue<boolean>,
    resolution: number,
    strokeColor: number[] = null,
  ): any {
    let value = this.getValue(feature, property);
    if (Array.isArray(value)) {
      if (typeof value[0] === 'string') {
        if (value.length > 1) {
          value = this.getColorFromString(feature, value[0], value[1]);
        } else {
          value = this.getColorFromString(feature, value[0]);
        }
      }
    } else if (typeof value === 'string') {
      value = this.getColorFromString(feature, value);
    }
    const isInvisibleObject = this.getValue(feature, isInvisibleProperty);
    const isInvisibleValue: boolean = isInvisibleObject as any;
    if (typeof value !== 'object') {
      if (isInvisibleValue) {
        return this.applyInvisibility([0, 0, 0], isInvisibleValue);
      } else {
        return value;
      }
    } else if (Array.isArray(value)) {
      return this.applyInvisibility(value, isInvisibleValue);
    }

    const c: any = value;

    if (c?.stroke) strokeColor = c.stroke;
    strokeColor = this.applyInvisibility(strokeColor, isInvisibleValue);
    let fillColor: number[] = null;
    if (c?.fill) fillColor = c.fill;
    fillColor = this.applyInvisibility(fillColor, isInvisibleValue);
    const path: string = c?.path;

    if (path) return this.getFillFunction(feature, resolution, strokeColor, fillColor, path, c.width, c.height);
    else if (fillColor) return fillColor;
    else return strokeColor;
  }

  private getFillFunction(
    feature: any,
    resolution: number,
    strokeColor: number[],
    fillColor: number[],
    path: string,
    width: number,
    height: number,
  ): any {
    let factor = MapLoader.getSizeFactor(resolution);
    const pixelRatio = ol.has.DEVICE_PIXEL_RATIO;
    if (pixelRatio != null) factor *= pixelRatio;

    if (width * factor < 1 || height * factor < 1) return fillColor;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const defaultLineWidth = 0.05;

    const pattern = function () {
      canvas.width = width * factor;
      canvas.height = height * factor;
      ctx.lineWidth = defaultLineWidth * factor;
      if (ctx.lineWidth < 1.0) ctx.lineWidth = 1.0;

      if (strokeColor != null) ctx.strokeStyle = this.getRgbColor(strokeColor);

      if (fillColor != null) ctx.fillStyle = this.getRgbColor(fillColor);

      ctx.fill();

      const arr = (path ?? '').split(' ');
      const actions = [];
      for (let i = 0; i < arr.length; i++) {
        let s = arr[i];
        if (s != null && s !== '') {
          const c = s[0];
          if (c === 'm' || c === 'M') {
            actions.push([ctx.beginPath, []]);
            actions.push([ctx.moveTo, []]);
            s = s.substring(1);
          } else if (c === 'l' || c === 'L') {
            actions.push([ctx.lineTo, []]);
            s = s.substring(1);
          } else if (c === 'a' || c === 'A') {
            actions.push([ctx.arcTo, []]);
            s = s.substring(1);
          } else if (c === 'z' || c === 'Z') {
            actions.push([ctx.closePath, []]);
            s = s.substring(1);
          }
          s = s.trim();
          if (s && s !== '') {
            const nums = s.split(',');
            for (let j = 0; j < nums.length; j++) {
              const num = nums[j].trim();
              if (num != null && num !== '') {
                actions[actions.length - 1][1].push(Number(num) * factor);
              }
            }
          }
        }
      }
      for (let k = 0; k < actions.length; k++) {
        const action = actions[k];
        const func = action[0];
        const args = action[1];
        if (args.length === 3) func.call(ctx, args[0], args[1], args[2]);
        else if (args.length === 2) func.call(ctx, args[0], args[1]);
        else if (args.length === 1) func.call(ctx, args[0]);
        else func.call(ctx);
      }
      ctx.fill();
      ctx.stroke();

      return ctx.createPattern(canvas, 'repeat');
    }.bind(this)();

    return pattern;
  }

  private getColorFromString(feature: any, hex: string, alpha: number = null) {
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      let c = hex
        .substring(1)
        .split('')
        .map((_) => this.parseHex(_));
      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      const newcolor: number[] = [];
      for (let i = 0; i < c.length / 2; i++) {
        newcolor.push(c[i * 2] * 16 + c[i * 2 + 1]);
      }
      c = newcolor;

      if (alpha) {
        if (c.length < 4) c.push(alpha);
        else c[3] = alpha;
      } else if (c.length === 4) {
        c[3] /= 255;
      }
      return c;
    } else {
      let c = this.getMappedValue(feature, new Object(hex), null, 'Color');
      if (!Array.isArray(c)) {
        c = [255, 255, 255, 0];
        try {
          feature.changed();
        } catch (e) {}
      }
      return c;
    }
  }

  private parseHex(hex: string): number {
    let result: number = 0;
    for (let i = 0; i < hex.length; i++) {
      const c = hex[hex.length - 1 - i];
      let num: number;
      switch (c) {
        case 'A':
        case 'a':
          num = 10;
          break;
        case 'B':
        case 'b':
          num = 11;
          break;
        case 'C':
        case 'c':
          num = 12;
          break;
        case 'D':
        case 'd':
          num = 13;
          break;
        case 'E':
        case 'e':
          num = 14;
          break;
        case 'F':
        case 'f':
          num = 15;
          break;
        default:
          num = parseInt(c);
          break;
      }
      result += num << (i * 4);
    }
    return result;
  }

  private applyInvisibility(color: number[], isInvisible: boolean): number[] {
    if (color && isInvisible) {
      color = [color[0], color[1], color[2], 0];
    }
    return color;
  }

  private getRgbColor(color: number[]): string {
    if (color.length === 3) return 'rgb(' + color[0] + ',' + color[1] + ',' + color[2] + ')';
    else if (color.length === 4) return 'rgba(' + color[0] + ',' + color[1] + ',' + color[2] + ',' + color[3] + ')';
    else return null;
  }

  private getValue(feature: any, property: any, factor: number = null): object {
    if (property == null) return null;

    let value: object = null;
    if (typeof property !== 'object' || Array.isArray(property)) value = property;
    else if (property.valueColumnNames != null && property.valueColumnNames.length > 0) {
      if (property.valueColumnNames.length === 1) {
        let columnName: string = property.valueColumnNames[0];
        let arr = columnName.split('*');
        if (arr.length > 1) {
          if (!factor) {
            factor = 1;
          }
          for (let i = 1; i < arr.length; i++) {
            factor *= MapLoader.toNumber(arr[i].trim());
          }
          columnName = arr[0].trim();
        }
        arr = columnName.split('/');
        if (arr.length > 1) {
          if (!factor) {
            factor = 1;
          }
          for (let i = 1; i < arr.length; i++) {
            factor /= MapLoader.toNumber(arr[i].trim());
          }
          columnName = arr[0].trim();
        }
        arr = columnName.split('+');
        if (arr.length > 1) {
          if (!factor) {
            factor = 1;
          }
          for (let i = 1; i < arr.length; i++) {
            factor += MapLoader.toNumber(arr[i].trim());
          }
          columnName = arr[0].trim();
        }
        arr = columnName.split('-');
        if (arr.length > 1) {
          if (!factor) {
            factor = 1;
          }
          for (let i = 1; i < arr.length; i++) {
            factor -= MapLoader.toNumber(arr[i].trim());
          }
          columnName = arr[0].trim();
        }
        value = this.getMappedValue(feature, feature.get(columnName), property.valueMap, property.valueTransformType);
      } else {
        const arr = [];
        for (let i = 0; i < property.valueColumnNames.length; i++) {
          arr.push(feature.get(property.valueColumnNames[i]));
        }
        value = arr;
      }
    } else {
      value =
        this.getMappedValue(feature, property.value as any, property.valueMap, property.valueTransformType) ?? property;
    }

    return this.applyFactor(value, factor);
  }

  private applyFactor(value: object, factor: number): object {
    if (factor != null) {
      if (Array.isArray(value)) {
        const arr: object[] = this.deepClone(value);
        value = arr;
        for (let i = 0; i < arr.length; i++) {
          value[i] *= factor;
          if (isNaN(value[i])) value[i] = 0.0;
        }
      } else {
        let num = (value as any) * factor;
        if (isNaN(num)) num = 0.0;
        value = num as any;
      }
    }
    return value;
  }

  private cachedValues = {};
  private cachedFeatures: Map<string, any> = new Map<string, any>();
  private getMappedValue(feature: any, value: object, valueMap: string[][], transformType: string): object {
    if (((valueMap == null || valueMap.length === 0) && !transformType) || !value) {
      return value;
    }

    let result: string = value as any;

    if (transformType) {
      const msg = {
        PackageId: this.packageId,
        MapNames: this.mapNames,
        Type: transformType,
        Values: [result],
      };
      let key = this.packageId ?? '';
      if (this.mapNames) {
        for (const map of this.mapNames) {
          key += '#' + map;
        }
      }
      key += '+' + transformType;
      key += '|' + result;
      result = this.cachedValues[key];
      if (result === '') {
        result = null;
      } else if (!result) {
        let list: any[] = this.cachedFeatures.get(key);
        if (list) {
          list.push(feature);
        } else {
          this.cachedFeatures.set(key, [feature]);
          this.coreDataService.executeWebApiPOSTMethod(this.transformMapValueUrl, msg).subscribe((_) => {
            this.cachedValues[key] = _ ?? '';
            let features = this.cachedFeatures.get(key);
            if (features) {
              this.cachedFeatures.delete(key);
              for (const f of features) {
                try {
                  f.changed();
                } catch (e) {}
              }
            }
          });
        }
      }
    }

    if (valueMap) {
      for (let i = 0; i < valueMap.length; i++) {
        if (valueMap[i][0] === result) {
          result = valueMap[i][1];
          break;
        }
      }
    }

    return Object(result);
  }

  public static getSizeFactor(resolution: number): number {
    return ol.proj.Units.METERS_PER_UNIT['m'] / resolution;
  }

  public static getTextSizeFactor(resolution: number): number {
    return (this.getSizeFactor(resolution) * 4.0) / 3.0 / this.defaultFontSize;
  }

  // tslint:disable-next-line: member-ordering
  public static getResolutionFromScale(scale: number): number {
    return scale / MapLoader.getDpm();
  }

  // tslint:disable-next-line: member-ordering
  public static getScaleFromResolution(resolution: number): number {
    return resolution * MapLoader.getDpm();
  }

  // tslint:disable-next-line: member-ordering
  static getDpm(): number {
    const dpi = this.getDpi();
    const dpm = ((dpi * 1000) / 25.4) * ol.proj.Units.METERS_PER_UNIT['m'];
    return dpm;
  }

  // tslint:disable-next-line: member-ordering
  static getDpi(): number {
    let dpi = ol.has.DEVICE_PIXEL_RATIO * 72;
    if (dpi == null) dpi = 72;
    return dpi;
  }

  public buildExtentFilter(
    extent: number[],
    additionalExtentOrCondition: string,
    parameters: ParameterValue[],
  ): string {
    if (!extent?.length || isNaN(extent[0])) {
      return null;
    }
    let extentfilter = '';
    if (extent[0] > -Infinity && extent[1] > -Infinity && extent[2] < Infinity && extent[3] < Infinity) {
      CollectionHelper.removeByFilter(
        parameters,
        (_) => _.Name === 'EMaxX' || _.Name === 'EMaxY' || _.Name === 'EMinX' || _.Name === 'EMinY',
      );
      parameters.push(new ParameterValue('EMaxX', extent[0]));
      parameters.push(new ParameterValue('EMaxY', extent[1]));
      parameters.push(new ParameterValue('EMinX', extent[2]));
      parameters.push(new ParameterValue('EMinY', extent[3]));
      extentfilter = 'EMaxX>=$EMaxX AND EMaxY>=$EMaxY AND EMinX<=$EMinX AND EMinY<=$EMinY';
      if (additionalExtentOrCondition) {
        extentfilter = '(' + extentfilter + ') OR (' + additionalExtentOrCondition + ')';
      }
    } else {
      extentfilter = additionalExtentOrCondition;
    }
    return extentfilter;
  }

  public prepareObjectSpaceQueryWithExtent(
    query: QueryObjectsModel,
    extent: number[],
    additionalExtentOrCondition: string,
  ): QueryObjectsModel {
    if (!query.Parameters) {
      query.Parameters = [];
    }
    const extentfilter = this.buildExtentFilter(extent, additionalExtentOrCondition, query.Parameters);

    if (extentfilter !== '') {
      query = this.deepClone(query);
      let mainfilter = query.ObjectQueries[0].OPath;
      if (mainfilter == null || mainfilter === '') {
        mainfilter = extentfilter;
      } else {
        mainfilter = '(' + mainfilter + ') AND (' + extentfilter + ')';
      }
      query.ObjectQueries[0].OPath = mainfilter;
    }
    if (this.currentMapQueryParameters?.length) {
      for (const p of this.currentMapQueryParameters) {
        if (!query.Parameters.find((_) => _.Name === p.Name)) {
          query.Parameters.push(p);
        }
      }
    }
    return query;
  }

  private loadShpFeatures(
    layer: MapLayer,
    source: any,
    dataSource: string,
    objectClassName: string,
    filter: string,
    partitionColumnNames: string[],
    tooltipColumnNames: string[],
    extent: number[],
    dataProjection: any,
    featureProjection: any,
  ): Observable<void> {
    this.layersCurrentlyLoading.push(layer);
    let subject = new Subject<void>();
    if (this.enforcedViewProjection && extent) {
      extent = ol.proj.transformExtent(extent, this.enforcedViewProjection, dataProjection);
    } else if (featureProjection && extent) {
      extent = ol.proj.transformExtent(extent, featureProjection, dataProjection);
    }
    const url = this.readMapImageUrl.replace('ReadMapImage', 'ReadMapDataRows');
    const data = {
      PackageId: this.packageId,
      MapNames: this.mapNames,
      DataSource: dataSource,
      ObjectClassName: objectClassName,
      Filter: filter,
      Viewbox: extent,
      PartitionColumnNames: partitionColumnNames,
      TooltipColumnNames: tooltipColumnNames,
    };
    this.coreDataService
      .executeWebApiPOSTMethod(url, data)
      .pipe(map((data) => this.readFeatures(data, dataProjection, featureProjection, layer.identityColumnName)))
      .subscribe(
        (features) => {
          if (features) {
            source.addFeatures(features);
          }
          CollectionHelper.removeItem(this.layersCurrentlyLoading, layer);
          subject.next();
          subject.complete();
        },
        (_) => {
          CollectionHelper.removeItem(this.layersCurrentlyLoading, layer);
          if (isDevMode()) {
            console.error(_);
          }
          if (extent) {
            source.removeLoadedExtent(extent);
          }
          subject.next();
          subject.complete();
        },
      );

    return subject.asObservable();
  }

  public prepareObjectSpaceExtent(extent: number[], dataProjection?: any): number[] {
    if (!dataProjection) {
      dataProjection = this.dataViewProjection;
    }
    if (extent?.length && !isNaN(extent[0]) && this.enforcedViewProjection && dataProjection) {
      extent = ol.proj.transformExtent(extent, this.enforcedViewProjection, dataProjection);
    }
    return extent;
  }

  private loadObjectSpaceFeatures(
    layer: MapLayer,
    name: string,
    source: any,
    query: any,
    dataProjection: any,
    extent: number[],
    featureProjection: any,
  ): void {
    this.layersCurrentlyLoading.push(layer);
    extent = this.prepareObjectSpaceExtent(extent, dataProjection);
    let loadFeatures: Observable<any>;
    if (CacheStorageAdapter.useOfflineMode) {
      loadFeatures = this.loadObjectSpaceFeaturesFromCache(name, extent);
    } else {
      loadFeatures = this.queryObjectSpaceObjectsByExtent(query, extent).pipe(
        catchError((_) => {
          return this.loadObjectSpaceFeaturesFromCache(name, extent);
        }),
      );
    }
    loadFeatures
      .pipe(
        map((data) => {
          return {
            data: data,
            features: this.readFeatures(data, dataProjection, featureProjection, layer.identityColumnName),
          };
        }),
      )
      .pipe(
        concatMap((data) => {
          if (data?.features?.length) {
            source.addFeatures(data?.features);
          }

          CollectionHelper.removeItem(this.layersCurrentlyLoading, layer);

          if (
            data?.features?.length &&
            this.queryCache &&
            this.dataEntitiesToRefresh?.length &&
            !CacheStorageAdapter.useOfflineMode
          ) {
            const cacheData: { key: string; value: any }[] = [];
            for (let i = 0; i < data?.features?.length; i++) {
              const id = data.features[i].getId();
              const json = data.data.features[i];
              if (id && json) {
                cacheData.push({ key: id, value: json });
              }
            }
            return from(this.queryCache.saveFeaturesById(name, cacheData, this.dataEntitiesToRefresh));
          }
          return of();
        }),
      )
      .subscribe(
        () => {},
        (_) => {
          CollectionHelper.removeItem(this.layersCurrentlyLoading, layer);
          source.removeLoadedExtent(extent);
          this.layersWithLoadErrors.push(layer);
          this.olmap.updateSize();
        },
      );
  }

  private loadObjectSpaceFeaturesFromCache(name: string, extent: number[]): Observable<any> {
    if (this.queryCache) {
      return from(this.queryCache.tryLoadFeatures(name, extent));
    } else {
      return of(null);
    }
  }

  public queryObjectSpaceObjectsByExtent(
    query: QueryObjectsModel,
    extent: number[],
    additionalExtentOrCondition?: string,
  ): Observable<any> {
    const preparedQuery = this.prepareObjectSpaceQueryWithExtent(query, extent, additionalExtentOrCondition);
    return this.abcCore.query.readObjects(preparedQuery, undefined, true);
  }

  public getObjectSpaceFeatures(
    layer: any,
    entities: DataEntity[],
    dataProjection: any,
    featureProjection: any,
  ): Observable<any> {
    let loadFeatures: Observable<any>;
    if (CacheStorageAdapter.useOfflineMode) {
      loadFeatures = this.loadObjectSpaceFeaturesForEntitiesFromCache(layer.fullName, entities);
    } else {
      loadFeatures = this.queryObjectSpaceFeatures(layer, entities, dataProjection, featureProjection).pipe(
        catchError((_) => {
          return this.loadObjectSpaceFeaturesForEntitiesFromCache(layer, entities);
        }),
      );
    }
    return loadFeatures.pipe(
      map((data) => {
        return this.readFeatures(data, dataProjection, featureProjection, layer.layerdefinition?.identityColumnName);
      }),
    );
  }

  private loadObjectSpaceFeaturesForEntitiesFromCache(name: string, entities: DataEntity[]): Observable<any> {
    if (this.queryCache) {
      return from(this.queryCache.tryLoadFeaturesForEntities(name, entities));
    } else {
      return of(null);
    }
  }

  private queryObjectSpaceFeatures(
    layer: any,
    entities: DataEntity[],
    dataProjection: any,
    featureProjection: any,
  ): Observable<any> {
    const src = layer.getSource();
    const query: QueryObjectsModel = this.deepClone(src.layerdefinition.query);
    return this.abcCore.query.readObjects(
      query,
      entities.map((_) => _.id),
      true,
    );
  }

  private deepClone(obj: any): any {
    return ObjectHelper.cloneDeep(obj);
  }

  public readFeatures(data: any, dataProjection: any, featureProjection: any, identityColumnName: string): any {
    if (data?.features) {
      for (const f of data.features) {
        if (identityColumnName) {
          f.id = f.properties[identityColumnName];
        }
        if (!f.id) {
          f.id = f.properties['GID'] ?? f.properties['_rowid'] ?? Object.values(f.properties)[0];
        }
        this.prepareCoordinates(f.geometry?.coordinates);
      }
      if (dataProjection && featureProjection && dataProjection != featureProjection) {
        return this.geoJsonFormat.readFeatures(data, {
          dataProjection: dataProjection,
          featureProjection: featureProjection,
        });
      } else {
        return this.geoJsonFormat.readFeatures(data);
      }
    }
  }

  private prepareCoordinates(coordinates: any[]): number {
    if (coordinates?.length > 0 && Array.isArray(coordinates[0])) {
      let min: number = undefined;
      let hasNotMin: boolean = false;
      for (const item of coordinates) {
        const num = this.prepareCoordinates(item);
        if (num > 0) {
          if (min === undefined) {
            min = num;
          } else if (min > num) {
            min = num;
            hasNotMin = true;
          } else if (min < num) {
            hasNotMin = true;
          }
        }
      }
      if (hasNotMin) {
        for (let i = 0; i < coordinates.length; i++) {
          while (coordinates[i].length > min) {
            CollectionHelper.removeAt(coordinates[i], coordinates[i].length - 1);
          }
        }
      }
    } else {
      return coordinates?.length;
    }
  }

  public getVectorLayers(): any[] {
    return this.getLayers(true);
  }

  public getAllLayers(): any[] {
    return this.getLayers(false);
  }

  private getLayers(vectorLayersOnly: boolean, onlyVisible?: boolean): any[] {
    return this.getLayersRecursive(this.olmap.getLayers().getArray(), vectorLayersOnly, onlyVisible);
  }

  private getLayersRecursive(layers: any[], vectorLayersOnly: boolean, onlyVisible?: boolean): any[] {
    const ret = [];

    for (const l of layers) {
      if (!onlyVisible || l.getVisible()) {
        if (l instanceof ol.layer.Group) {
          const retSub = this.getLayersRecursive(l.getLayers().getArray(), vectorLayersOnly, onlyVisible);
          ret.push(...retSub);
        } else if (l instanceof ol.layer.Vector || l instanceof ol.layer.VectorTile || !vectorLayersOnly) {
          ret.push(l);
        }
      }
    }

    return ret;
  }

  public canReadLayerExtent(layer: any): boolean {
    return layer?.layerdefinition?.type === MapLayerType[MapLayerType.ObjectSpace];
  }

  public readLayerExtent(layer: any): Observable<[number, number, number, number]> {
    const query = layer?.layerdefinition?.query?.ObjectQueries[0];
    if (query) {
      const parameters = layer?.layerdefinition?.query?.Parameters;
      let path = 'Type(' + query.ObjectType + ')';
      let filter = query.OPath;
      if (query.ObjectQueries?.length > 0) {
        const subpath = query.ObjectQueries[0].OPath;
        if (subpath && subpath !== '') {
          const subfilter = 'Exists(' + subpath + ')';
          if (filter && filter !== '') {
            filter = '(' + filter + ') And ' + subfilter;
          } else {
            filter = subfilter;
          }
        }
      }
      if (filter && filter !== '') {
        path += '[' + filter + ']';
      }

      const minX = new QueryValueModel();
      minX.OPath = path + '.Min(EMinX)';
      minX.Parameters = parameters;
      const maxX = new QueryValueModel();
      maxX.OPath = path + '.Max(EMaxX)';
      maxX.Parameters = parameters;
      const minY = new QueryValueModel();
      minY.OPath = path + '.Min(EMinY)';
      minY.Parameters = parameters;
      const maxY = new QueryValueModel();
      maxY.OPath = path + '.Max(EMaxY)';
      maxY.Parameters = parameters;

      return forkJoin([
        this.coreDataService.executeReadValueQuery(minX).pipe(map((_) => MapLoader.toNumber(_))),
        this.coreDataService.executeReadValueQuery(minY).pipe(map((_) => MapLoader.toNumber(_))),
        this.coreDataService.executeReadValueQuery(maxX).pipe(map((_) => MapLoader.toNumber(_))),
        this.coreDataService.executeReadValueQuery(maxY).pipe(map((_) => MapLoader.toNumber(_))),
      ]);
    }
    return null;
  }

  private static toNumber(value: any): number {
    return value as number;
  }

  public static normalizeRotation(value: any): number {
    let angle = this.toNumber(value);
    while (angle < 0) {
      angle += 2 * Math.PI;
    }
    while (angle >= 2 * Math.PI) {
      angle -= 2 * Math.PI;
    }

    return angle;
  }

  public getObjectTypeFromLayer(layer: any): string {
    const query = layer?.layerdefinition?.query?.ObjectQueries[0];
    if (query) {
      return query.ObjectType;
    }
    return null;
  }

  public static updateLayerResolutions(layers: any[], factor: number) {
    for (const l of layers) {
      if (l.getMinResolution() > 0) {
        l.setMinResolution(l.getMinResolution() * factor);
      }
      if (l.getMaxResolution() > 0) {
        l.setMaxResolution(l.getMaxResolution() * factor);
      }
    }
  }

  private getDataEntityInfos(entites: DataEntity[]): Observable<DataEntityInfo[]> {
    if (CacheStorageAdapter.useOfflineMode) {
      return this.getDataEntityInfosFromCache(entites);
    } else {
      return this.queryDataEntityInfos(entites).pipe(
        catchError((_) => {
          return this.getDataEntityInfosFromCache(entites);
        }),
      );
    }
  }

  private getDataEntityInfosFromCache(entites: DataEntity[]): Observable<DataEntityInfo[]> {
    if (this.queryCache) {
      return from(this.queryCache.tryLoadDataEntityInfos(entites));
    } else {
      return of(null);
    }
  }

  public queryDataEntityInfos(entites: DataEntity[]): Observable<DataEntityInfo[]> {
    const url = this.readMapImageUrl.replace('ReadMapImage', 'ReadMapObjectsInfo');
    const query = {
      ObjectKeys: entites.map((_) => {
        return { Id: _.id, ObjectType: _.objectType };
      }),
      Parameters: this.currentMapQueryParameters,
    };
    const arrInfos = this.abcCore.data
      .executeWebApiPOSTMethod(url, query)
      .pipe(
        map((data) => {
          const arr: DataEntityInfo[] = [];
          if (Array.isArray(data)) {
            for (const item of data as any) {
              let category = item.objectType;
              let displayText = item.displayText;
              if (item.ObjectSets?.length) {
                category = item.ObjectSets[0].name;
                displayText = item.ObjectSets[0].displayText;
              }
              arr.push(
                new DataEntityInfo(
                  new DataEntity(item.objectType, item.id),
                  category,
                  displayText,
                  item.CommandsBadgeValue,
                  item.CommandsEnabled,
                ),
              );
            }
          }
          return arr;
        }),
      )
      .pipe(
        concatMap((_) => {
          if (_?.length) {
            return forkJoin(
              _.map((info) =>
                this.abcCore.objectSpace.getObjectType(info.entity.objectType).pipe(
                  map((ot) => {
                    info.category = ot.prepareDisplayTextPlural(info.category);
                    return info;
                  }),
                ),
              ),
            );
          }
          return of([]);
        }),
      );
    return arrInfos;
  }

  private getTargetOfObjectSet(objectSetName: string): MapTarget {
    for (const l of this.getAllLayers()) {
      const ld: MapLayer = l.layerdefinition;
      if (ld?.target?.url && objectSetName === ld.fullName) {
        return ld.target;
      }
    }
  }

  public renderMap(
    dpi: number,
    finished: (errorLoadingLayers: MapLayer[]) => void,
    onStateChange: (state: string) => void,
  ): void {
    this.mapService.ensureLegendInitialized().then(() => {
      this.layersWithLoadErrors = new Array<MapLayer>();
      this.renderMapDpi = dpi;
      const events: string[] = [];
      const workingLayers: MapLayer[] = [];

      const renderCompleteOrErrorLogic = (ell: MapLayer[]) => {
        if (
          this.cachedFeatures.size === 0 &&
          this.layersCurrentlyLoading.length === 0 &&
          !this.lastMapLabelPlacer?.hasPendingStyles
        ) {
          for (const eventid of events) {
            ol.Observable.unByKey(eventid);
          }
          this.renderMapDpi = null;
          if (finished) {
            finished(ell);
          }
          this.layersWithLoadErrors = new Array<MapLayer>();
        }
      };

      const layerPreLogic = (layerDef: MapLayer) => {
        if (this.layersWithLoadErrors.length > 0) {
          renderCompleteOrErrorLogic(this.layersWithLoadErrors);
          this.layersWithLoadErrors = new Array<MapLayer>();
        } else {
          workingLayers.push(layerDef);
          onStateChange(this.res.get('Map-RenderingLayer', workingLayers[0].name));
        }
      };

      const layerPostLogic = async (layerDef: MapLayer) => {
        if (this.layersWithLoadErrors.length > 0) {
          renderCompleteOrErrorLogic(this.layersWithLoadErrors);
          this.layersWithLoadErrors = new Array<MapLayer>();
        } else {
          CollectionHelper.removeItem(workingLayers, layerDef);
          if (workingLayers.length > 0) {
            onStateChange(this.res.get('Map-RenderingLayer', workingLayers[0].name));
          }
        }
      };

      if (onStateChange) {
        for (const ly of this.getLayers(false, true)) {
          if (ly.layerdefinition) {
            const layerDef: MapLayer = ly.layerdefinition as MapLayer;
            events.push(ly.on('prerender', () => layerPreLogic(layerDef)));
            events.push(ly.on('postrender', () => layerPostLogic(layerDef)));
            const source = ly.getSource();
            if (source) {
              if (source.tileLoadFunction) {
                events.push(source.on('tileloadstart', () => layerPreLogic(layerDef)));
                events.push(source.on('tileloadend', () => layerPostLogic(layerDef)));
              } else if (source.imageLoadFunction_) {
                events.push(source.on('imageloadstart', () => layerPreLogic(layerDef)));
                events.push(source.on('imageloadend', () => layerPostLogic(layerDef)));
              } else if (source.loader_) {
                events.push(source.on('featuresloadstart', () => layerPreLogic(layerDef)));
                events.push(source.on('featuresloadend', () => layerPostLogic(layerDef)));
              }
            }
          }
        }
      }
      events.push(this.olmap.on('error', () => renderCompleteOrErrorLogic(this.layersWithLoadErrors)));
      events.push(this.olmap.on('rendercomplete', () => renderCompleteOrErrorLogic(this.layersWithLoadErrors)));
    });
  }

  public tryGetTooltips(feature: any, layer: any): { layerName: string; values: Object } {
    let result: Object = null;
    let layerName: string = null;
    const ml: MapLayer = layer?.layerdefinition;
    if (ml != null && ml.columns != null) {
      result = {};
      layerName = ml.displayText ?? ml.name;
      for (let c of ml.columns) {
        if (c.useForTooltip) {
          const value = feature?.get(c.name);
          if (value) {
            const cname = c.displayName ?? c.name;
            result[cname] = value;
          }
        }
      }
    }
    if (result) {
      return { layerName: layerName, values: result };
    }
  }

  public setDataEntitiesToRefresh(entities: DataEntity[]): void {
    if (entities) {
      for (const entity of entities) {
        if (!this.dataEntitiesToRefresh.find((_) => _.id === entity.id)) {
          this.dataEntitiesToRefresh.push(entity);
        }
      }
    }
  }

  public queryNetworkData(edgeObjectType: string): Observable<object[]> {
    const query = new NetworkTraceModel();
    query.EdgeObjectType = edgeObjectType;
    return this.coreDataService.queryNetworkData(query);
  }

  public queryShortestPath(model: NetworkTraceModel): Observable<NetworkTraceObject[]> {
    if (CacheStorageAdapter.useOfflineMode) {
      return this.queryShortestPathFromCache(model);
    } else {
      return this.abcCore.data.queryShortestPath(model).pipe(
        catchError((_) => {
          return this.queryShortestPathFromCache(model);
        }),
      );
    }
  }

  private queryShortestPathFromCache(model: NetworkTraceModel): Observable<NetworkTraceObject[]> {
    const tracer = new MapNetTracer(
      model.EdgeObjectType,
      (key) => this.queryCache.tryLoadNetworkItem(key),
      async (ot) => (await this.abcCore.objectSpace.getObjectType(ot).toPromise())?.name,
    );
    return from(tracer.search(model.StartEdgeIds, model.StartNodeIds, model.EndEdgeIds, model.EndNodeIds, true));
  }

  public static toDataEntity(feature: any, keyOnly?: boolean): DataEntity {
    if (feature) {
      const oId = this.getFeatureObjectId(feature);
      const oType = this.getFeatureObjectType(feature);
      if (oId && oType) {
        if (keyOnly) {
          return new DataEntity(oType, oId);
        } else {
          return new DataEntity(oType, oId, feature);
        }
      }
    }
  }

  public static toLayerEntity(feature: any, layerdefinition: MapLayer): DataEntity {
    if (feature && layerdefinition) {
      const id = feature.getId();
      if (id) {
        return new DataEntity(layerdefinition.fullName, id, feature);
      }
    }
  }

  public static getFeatureObjectId(feature: any): string {
    return this.getFeatureValue(feature, 'Id');
  }

  public static getFeatureObjectType(feature: any): string | number {
    return this.getFeatureValue(feature, 'ObjectType');
  }

  private static getFeatureValue(feature: any, propertyName: string): any {
    if (feature) {
      let value = feature.get(propertyName);
      if (!value) {
        value = feature.get(propertyName.toLowerCase());
      }
      if (!value) {
        value = feature.get(propertyName.toUpperCase());
      }
      return value;
    }
  }

  public setCustomColor(entities: DataEntity[], color: number[]): void {
    if (entities) {
      for (const e of entities) {
        if (color?.length) {
          if (!this.customObjectColors) {
            this.customObjectColors = new Map<string, number[]>();
          }
          this.customObjectColors.set(e.id, color);
        } else {
          if (this.customObjectColors) {
            this.customObjectColors.delete(e.id);
          }
        }
        if (this.queryCache) {
          this.queryCache.trySaveColor(e.id, color).then(() => {});
        }
      }
    } else {
      this.customObjectColors = null;
      if (this.queryCache) {
        this.queryCache.tryDeleteColors().then(() => {});
      }
    }
  }
}
